import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, Link } from "react-router-dom";
import { CalendarCheck, Plus, X, Trash2, ChevronRight, ChevronDown, CheckCircle2, RotateCcw, ListChecks, Link2, Archive, BarChart3, Star, StickyNote, Pencil, FolderKanban, Send, Check, Eye, EyeOff, ArrowUpRight, ArrowLeft, Trophy, Salad, FileText, Lightbulb, Hourglass, AlertTriangle, CalendarDays, Pin, Rocket, MessageSquare, Bot } from "lucide-react";
import { useApiData, apiFetch } from "../../lib/api.js";
import GradientHeader from "../../components/GradientHeader.jsx";
import {
  Stranka, EmptyState, Hlaska, KartaSeznam, PruhFiltru, FiltrRadek, Segment, Stitek, VyberSeznamu,
} from "../../components/ui.jsx";
import { ton as tonTypu, TON_STATUS } from "../../lib/tony.js";
import { getCurrentUser } from "../../lib/auth.js";
import MesicniPriority from "../../components/MesicniPriority.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { toast } from "../../components/Toast.jsx";
import { AddTaskModal, TaskDetailModal } from "./Ukoly.jsx";
import UkolPicker from "../../components/UkolPicker.jsx";
import RefreshButton from "../../components/RefreshButton.jsx";
import TextSOdkazy from "../../components/TextSOdkazy.jsx";
import { OTAZKY as HODNOCENI_OTAZKY, OdpovedPill } from "../../components/ClientHodnoceniSection.jsx";

// === OPERATIONS - Meetingy ===
// Typy: týdenní / měsíční / kvartální / roční. Šablony agendy a zápisu 1:1 z Notionu.
// Na meetingu se vybírají nebo zakládají úkoly z Operations (company_ukoly.meeting_id)
// a propisují se priority měsíce (existující komponenta MesicniPriority).

// 21.7.2026: stitky obdobi pro snadny filtr ocima - odvozene z DATA meetingu
// (ne z nazvu). Tydenni → W29, mesicni → M7, kvartalni → Q3, + rok u vsech.
function isoWeekFE(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
}
// Měsíční meeting hodnotí měsíc z NÁZVU (M07/2026 = červenec), i když se koná začátkem
// srpna — datum meetingu s hodnoceným měsícem nesouvisí (přání Ládi 29.7.). Fallback: datum.
function mesicKodZNazvu(nazev) {
  const mm = /M(\d{1,2})\/(\d{4})/i.exec(String(nazev || ""));
  if (!mm) return null;
  const mesic = parseInt(mm[1], 10), rok = parseInt(mm[2], 10);
  if (!(mesic >= 1 && mesic <= 12) || !(rok >= 2000)) return null;
  return { ym: `${rok}-${String(mesic).padStart(2, "0")}`, mTag: `M${mesic}`, rok };
}
// Svislé dělítko (jako u Google recenzí na home page) — nahrazuje tečku „·" v podnadpisech.
function Delitko() {
  return <span aria-hidden="true" className="inline-block w-px h-3 bg-current opacity-40 mx-1.5 align-middle" />;
}
// Období meetingu jako dvojice popisek + hodnota (5.9.2026: dřív to byly holé
// štítky „W37", „2026", u kterých nebylo poznat, co které číslo znamená).
function obdobiStitky(m) {
  if (!m?.datum) return [];
  const d = new Date(String(m.datum).slice(0, 10) + "T12:00:00");
  if (Number.isNaN(d.getTime())) return [];
  const out = [];
  if (m.typ === "tydenni") {
    out.push({ popisek: "Týden", hodnota: `W${isoWeekFE(d)}` });
    out.push({ popisek: "Rok", hodnota: String(d.getFullYear()) });
    return out;
  }
  if (m.typ === "mesicni") {
    const kod = mesicKodZNazvu(m.nazev);
    if (kod) {
      out.push({ popisek: "Měsíc", hodnota: kod.mTag });
      out.push({ popisek: "Rok", hodnota: String(kod.rok) });
      return out;
    }
    out.push({ popisek: "Měsíc", hodnota: `M${d.getMonth() + 1}` });
  } else if (m.typ === "kvartalni") {
    out.push({ popisek: "Kvartál", hodnota: `Q${Math.floor(d.getMonth() / 3) + 1}` });
  }
  out.push({ popisek: "Rok", hodnota: String(d.getFullYear()) });
  return out;
}

// Pruh „Typ | Týden | Rok" v hlavičce detailu. Na telefonu se zalomí pod název
// a datum a zabere celou šířku (schváleno 5.9.2026), na počítači zůstane
// v jednom řádku vedle nich. Barva prvního pole drží tón typu ze vzorníku.
function ObdobiPruh({ m }) {
  const typ = TYP_META[m?.typ] || TYP_META.tydenni;
  const casti = obdobiStitky(m);
  const bunka = "flex min-h-[44px] flex-col items-center justify-center px-2 py-1 leading-tight";
  return (
    <div className={`grid w-full shrink-0 overflow-hidden rounded-xl border border-slate-200 md:w-auto ${casti.length === 1 ? "grid-cols-2" : "grid-cols-3"}`}>
      <div className={`${bunka} ${tonTypu(typ.ton).pilulka} md:min-w-[108px]`}>
        <span className="text-[11px] font-semibold uppercase tracking-wide opacity-70">Typ</span>
        <span className="text-sm font-bold">{typ.label}</span>
      </div>
      {casti.map((c) => (
        <div key={c.popisek} className={`${bunka} border-l border-slate-200 bg-white md:min-w-[84px]`}>
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{c.popisek}</span>
          <span className="text-sm font-bold tabular-nums text-slate-700">{c.hodnota}</span>
        </div>
      ))}
    </div>
  );
}

// 4.9.2026 (fáze 4a): `ton` je klíč do src/lib/tony.js, ostatní pole zůstávají
// kvůli místům, která si kreslí vlastní pruh nebo pilulku.
const TYP_META = {
  tydenni: { label: "Týdenní", ton: "znacka", pill: "bg-brand-100 text-brand-700", ikona: "bg-brand-100 text-brand-700", bar: "bg-brand-500" },
  mesicni: { label: "Měsíční", ton: "info", pill: "bg-sky-100 text-sky-700", ikona: "bg-sky-100 text-sky-700", bar: "bg-sky-500" },
  kvartalni: { label: "Kvartální", ton: "pozor", pill: "bg-amber-100 text-amber-700", ikona: "bg-amber-100 text-amber-700", bar: "bg-amber-500" },
  rocni: { label: "Roční", ton: "ok", pill: "bg-emerald-100 text-emerald-700", ikona: "bg-emerald-100 text-emerald-700", bar: "bg-emerald-500" },
};
const STATUS_LABEL = {
  zasobnik: "Backlog", prioritni: "Prioritní", mesicni: "Měsíční", tydenni: "Týdenní",
  v_procesu: "V procesu", hotovo: "Hotovo", archiv: "Archiv",
};
const STATUS_PILL = {
  zasobnik: "bg-slate-100 text-slate-600",
  prioritni: "bg-rose-50 text-rose-700", mesicni: "bg-sky-50 text-sky-700",
  tydenni: "bg-brand-50 text-brand-700", v_procesu: "bg-amber-50 text-amber-700",
  hotovo: "bg-emerald-50 text-emerald-700", archiv: "bg-slate-100 text-slate-500",
};
const PRIO_PILL = {
  High: ["Vysoká", "bg-red-50 text-red-700"],
  Medium: ["Střední", "bg-amber-50 text-amber-700"],
  Low: ["Nízká", "bg-slate-100 text-slate-600"],
};

// 4.9.2026 (fáze 4a): třídy z návrhového systému místo vlastních kopií.
const btnGhost = "btn btn-ghost";

// Textarea, ktera roste s obsahem (5.8.2026, hlaseni CSM z meetingu #14:
// "nejde se dostat na zacatek zpravy kurzorem, trva to pul hodiny tam dojet,
// a nejde udelat hezkou poznamku, kdyz je hodne textu"). Poznamky k meetingu
// byvaly jednoradkovy <input> — u dlouheho textu byl videt jen vyrez a kurzor
// se musel projet cely retezec. Tady je videt cely text a Shift+Enter dela
// odstavce. Vyska se prepocitava pri kazde zmene hodnoty.
function AutoTextarea({ value, className = "", minHeight = 76, maxHeight = 420, ...rest }) {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(maxHeight, Math.max(minHeight, el.scrollHeight + 2)) + "px";
    el.style.overflowY = el.scrollHeight + 2 > maxHeight ? "auto" : "hidden";
  }, [value, minHeight, maxHeight]);
  return <textarea ref={ref} value={value} className={className} {...rest} />;
}

// Dotykove zarizeni (11.8.2026, bug z mobilu: "Na mobilu nelze ulozit poznamka
// na meeting"): mobilni klavesnice nemaji Shift a Enter z nich casto ani
// nedorazi (Android IME posila keyCode 229). Poznamky se proto na dotyku
// odesilaji TLACITKEM a Enter dela novy radek; na desktopu Enter dal odesila.
const jeDotykoveZarizeni = () =>
  typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(pointer: coarse)").matches;

function fmtCz(iso) {
  if (!iso) return "—";
  const p = String(iso).slice(0, 10).split("-");
  return p.length === 3 ? `${Number(p[2])}. ${Number(p[1])}. ${p[0]}` : iso;
}

// Statistiky minulého týdne (po–ne) pro týdenní meeting — data z /api/data-weekly:
// reklama (útrata, leady, CPL), trialy (realizované, zaplacené, obrat) a noví klienti.
// StatTile (20.7.2026 v2): mírně větší písmo; doplňkové údaje jako PILL BOXY
// (pole `pills`), žádné tečkové oddělovače. `sub` zůstává pro jednoduchý text.
function StatTile({ label, value, sub, pills, barva }) {
  const stylTile = {
    sky: "bg-sky-50 border-sky-100",
    amber: "bg-amber-50 border-amber-100",
    emerald: "bg-emerald-50 border-emerald-100",
  }[barva] || "bg-slate-50 border-slate-100";
  const stylText = { sky: "text-sky-700", amber: "text-amber-700", emerald: "text-emerald-700" }[barva] || "text-slate-700";
  const stylMuted = { sky: "text-sky-600", amber: "text-amber-600", emerald: "text-emerald-600" }[barva] || "text-slate-500";
  const stylPill = {
    sky: "bg-white/80 text-sky-700 border-sky-200",
    amber: "bg-white/80 text-amber-700 border-amber-200",
    emerald: "bg-white/80 text-emerald-700 border-emerald-200",
  }[barva] || "bg-white/80 text-slate-600 border-slate-200";
  const pillList = (pills || []).filter(Boolean);
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${stylTile}`}>
      <div className={`text-[11px] font-semibold uppercase tracking-wide ${stylMuted}`}>{label}</div>
      <div className={`text-xl font-extrabold tabular-nums leading-tight mt-0.5 ${stylText}`}>{value}</div>
      {pillList.length > 0 ? (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {pillList.map((p, i) => (
            <span key={i} className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full border tabular-nums ${stylPill}`}>{p}</span>
          ))}
        </div>
      ) : (sub && <div className={`text-[11px] mt-1 ${stylMuted}`}>{sub}</div>)}
    </div>
  );
}

function StatsMinulyTyden({ data, weekStart, refetch, refreshing }) {
  if (!weekStart) return null;
  const konec = (() => {
    const d = new Date(weekStart + "T12:00:00");
    d.setDate(d.getDate() + 6);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const fmtKc = (v) => `${Math.round(v || 0).toLocaleString("cs")} Kč`;
  const nedostupne = data && (data.status === "unavailable" || (data.error && !data.leads && data.leads !== 0));
  return (
    <div className="glass p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <BarChart3 className="w-5 h-5 text-slate-500" />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-slate-900">Statistiky minulého týdne</h3>
          <div className="text-xs text-slate-400">pondělí až neděle, {fmtCz(weekStart)} – {fmtCz(konec)}</div>
        </div>
        {data?.weekLabel && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{data.weekLabel}</span>}
        {/* 21.7.2026: rucni aktualizace dat (obejde 5min cache endpointu i localStorage) */}
        {refetch && <RefreshButton refetch={refetch} refreshing={!!refreshing} className="shrink-0" />}
      </div>
      {!data ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {[...Array(6)].map((_, i) => <div key={i} className="h-[68px] rounded-xl bg-slate-50 border border-slate-100 animate-pulse" />)}
        </div>
      ) : nedostupne ? (
        <div className="text-sm text-slate-400">{data.error || "Data pro tento týden nejsou k dispozici."}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <StatTile barva="sky" label="Reklama útrata" value={fmtKc(data.mktSpend)} />
          <StatTile barva="sky" label="Leady" value={data.leads ?? 0}
            sub={`${data.nqLeads || 0} NQ, ${(data.leads || 0) > 0 ? Math.round(((data.nqLeads || 0) / data.leads) * 100) : 0} % NQ`} />
          <StatTile barva="sky" label="CPL" value={fmtKc(data.cpl)} sub="cena za lead" />
          {/* Trialy spojené do jedné dlaždice: realizované / zaplacené / obrat */}
          <StatTile barva="amber" label="Trialy" value={`${data.realizedTrialCount ?? 0} / ${data.fapiTrialCount ?? 0}`}
            sub={`domluvené / zaplacené, obrat ${fmtKc(data.fapiTrialRevenue)}`} />
          <StatTile barva="emerald" label="Noví klienti" value={data.noviKlientiTyden ?? data.dealThisWeek ?? 0}
            sub="aktivní, trial v tomto týdnu" />
          <StatTile barva="emerald" label="Fakturace nových" value={fmtKc(data.noviKlientiFakturace)}
            sub="první faktury za spolupráci" />
        </div>
      )}
    </div>
  );
}

const MESICE_CZ = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
function fmtMesicCz(mk) {
  if (!mk) return "";
  const [y, mm] = String(mk).split("-").map(Number);
  return `${MESICE_CZ[(mm || 1) - 1]} ${y}`;
}
const fmtKcG = (v) => `${Math.round(v || 0).toLocaleString("cs")} Kč`;

// Statistiky MINULÉHO měsíce pro měsíční meeting — data z /api/data-monthly
// (obrat + plnění cílů, noví/ukončení klienti, MRR, marketing, ROI, trialy).
function StatsMesic({ data, mesic, refetch, refreshing }) {
  if (!mesic) return null;
  const nedostupne = data && (data.status === "unavailable" || (data.error && data.totalInvoicesRevenue == null));
  return (
    <div className="glass p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <BarChart3 className="w-5 h-5 text-slate-500" />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-slate-900">Statistiky měsíce</h3>
          <div className="text-xs text-slate-400">{fmtMesicCz(mesic)}, obrat a plnění cílů se počítá automaticky</div>
        </div>
        {/* 21.7.2026: rucni aktualizace dat (obejde 5min cache endpointu i localStorage) */}
        {refetch && <RefreshButton refetch={refetch} refreshing={!!refreshing} className="shrink-0" />}
      </div>
      {!data ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[...Array(8)].map((_, i) => <div key={i} className="h-[68px] rounded-xl bg-slate-50 border border-slate-100 animate-pulse" />)}
        </div>
      ) : nedostupne ? (
        <div className="text-sm text-slate-400">{data.error || "Data pro tento měsíc nejsou k dispozici."}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatTile barva="sky" label="Obrat celkem (vč. DPH)" value={fmtKcG(data.totalInvoicesRevenue)}
            pills={[
              data.monthlyGoal > 0 ? `cíl ${fmtKcG(data.monthlyGoal)}` : "cíl nenastaven",
              data.monthlyGoal > 0 ? `${data.monthlyGoalPct} %` : null,
              data.totalInvoicesVat > 0 ? `DPH ${fmtKcG(data.totalInvoicesVat)}` : null,
            ]} />
          <StatTile barva="sky" label="Obrat noví klienti" value={fmtKcG(data.newClientRevenue)}
            pills={data.newClientGoal > 0 ? [`cíl ${fmtKcG(data.newClientGoal)}`, `${data.newClientGoalPct} %`] : ["cíl nenastaven"]} />
          <StatTile barva="emerald" label="Noví klienti" value={data.addedClients ?? 0} pills={[`${data.endedClients || 0} ukončených`]} />
          <StatTile barva="emerald" label="Aktivní klienti" value={data.activeClients ?? 0} pills={[`MRR ${fmtKcG(data.mrr)}`]} />
          <StatTile barva="amber" label="Marketing" value={fmtKcG(data.mktSpend)} pills={[`CPL ${fmtKcG(data.cpl)}`]} />
          <StatTile barva="amber" label="ROI marketingu" value={`${data.roiPct ?? 0} %`} pills={[`výnos ${fmtKcG(data.roiRevenue)}`]} />
          <StatTile barva="amber" label="Obrat z trialů" value={fmtKcG(data.trialRevenue)} pills={[`${data.trialCount || 0}× zaplacený trial`]} />
          {/* Ztracený obrat = neuzavření klienti (prohry × průměrný deal) — STEJNÁ logika jako CEO Monthly report. */}
          <StatTile barva="sky" label="Ztracený obrat" value={fmtKcG(data.lostRevenue)}
            pills={[`${data.trialLossCount || 0} proher × 6 700 Kč`, "neuzavření klienti"]} />
        </div>
      )}
    </div>
  );
}

// Statistiky KVARTÁLU pro kvartální report — součet 3 měsíců z /api/data-yearly
// + aktuální klientská základna a počet trenérů. Marže/retence/délka/hodnota
// klienta jsou ruční pole v zápisu (appka je zatím nespočítá).
function StatsKvartal({ yearly, info, treneri }) {
  if (!info) return null;
  const mesice = new Set(info.months);
  const radky = (yearly?.monthlyBreakdown || []).filter((x) => mesice.has(x.monthKey));
  const soucet = (klic) => radky.reduce((s, x) => s + (Number(x[klic]) || 0), 0);
  const cil = (yearly?.goalsByMonth || []).filter((g) => mesice.has(g.monthKey)).reduce((s, g) => s + (g.goal || 0), 0);
  const obrat = soucet("revenue");
  const leads = soucet("leads");
  const spend = soucet("mktSpend");
  return (
    <div className="glass p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <BarChart3 className="w-5 h-5 text-slate-500" />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-slate-900">Statistiky kvartálu Q{info.q} {info.year}</h3>
          <div className="text-xs text-slate-400">{fmtMesicCz(info.months[0])} – {fmtMesicCz(info.months[2])}, marži, retenci, délku spolupráce a hodnotu klienta doplň v zápisu</div>
        </div>
      </div>
      {!yearly ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[...Array(8)].map((_, i) => <div key={i} className="h-[68px] rounded-xl bg-slate-50 border border-slate-100 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatTile barva="sky" label="Obrat kvartálu" value={fmtKcG(obrat)}
            sub={cil > 0 ? `cíl ${fmtKcG(cil)}, ${Math.round((obrat / cil) * 100)} %` : "cíle nebyly nastaveny"} />
          <StatTile barva="sky" label="Obrat noví klienti" value={fmtKcG(soucet("newClientRevenue"))} />
          <StatTile barva="emerald" label="Noví klienti" value={soucet("addedClients")} sub={`${soucet("endedClients")} ukončených`} />
          <StatTile barva="emerald" label="Aktivní klientky" value={yearly.activeClientsCurrent ?? 0} sub="aktuálně" />
          <StatTile barva="emerald" label="Trenéři" value={(treneri?.people || []).length} sub="aktivní" />
          <StatTile barva="amber" label="Marketing" value={fmtKcG(spend)} sub={leads > 0 ? `CPL ${fmtKcG(Math.round(spend / leads))}` : ""} />
          <StatTile barva="amber" label="Leady" value={leads} sub={`${soucet("nqLeads")} NQ`} />
          <StatTile barva="amber" label="Obrat z trialů" value={fmtKcG(soucet("trialRevenue"))} sub={`${soucet("trialCount")}× trial`} />
        </div>
      )}
    </div>
  );
}

// Roční statistiky pro hodnocení roku — /api/data-yearly (obrat + plnění cílů,
// klientská základna, churn, marketing efektivita, MRR).
function StatsRok({ yearly, rok }) {
  if (!rok) return null;
  return (
    <div className="glass p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <BarChart3 className="w-5 h-5 text-slate-500" />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-slate-900">Roční statistiky {rok}</h3>
          <div className="text-xs text-slate-400">obrat, cíle, klienti a marketing za celý rok z appky</div>
        </div>
      </div>
      {!yearly ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[...Array(8)].map((_, i) => <div key={i} className="h-[68px] rounded-xl bg-slate-50 border border-slate-100 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatTile barva="sky" label="Obrat roku" value={fmtKcG(yearly.revenue)}
            sub={yearly.yearlyGoal > 0 ? `cíl ${fmtKcG(yearly.yearlyGoal)}, ${yearly.yearlyGoalPct} %` : "cíle nebyly nastaveny"} />
          <StatTile barva="sky" label="Obrat noví klienti" value={fmtKcG(yearly.newClientRevenue)} sub={`Ø deal ${fmtKcG(yearly.avgDealValue)}`} />
          <StatTile barva="emerald" label="Noví klienti" value={yearly.addedClients ?? 0} sub={`${yearly.endedClients || 0} ukončených, churn ${yearly.churnRate || 0} %`} />
          <StatTile barva="emerald" label="Aktivní klienti" value={yearly.activeClientsCurrent ?? 0} sub={`MRR ${fmtKcG(yearly.mrr)}`} />
          <StatTile barva="amber" label="Marketing" value={fmtKcG(yearly.mktSpend)} sub={`CPL ${fmtKcG(yearly.cpl)}, CAC ${fmtKcG(yearly.cac)}`} />
          <StatTile barva="amber" label="LTV" value={fmtKcG(yearly.ltv)} sub={`LTV:CAC ${yearly.ltvCacRatio || 0}`} />
          <StatTile barva="amber" label="Leady" value={yearly.leads ?? 0} sub={`${yearly.nqPct || 0} % NQ`} />
          <StatTile barva="amber" label="Obrat z trialů" value={fmtKcG(yearly.trialRevenue)} sub={`${yearly.trialCount || 0}× trial`} />
        </div>
      )}
    </div>
  );
}

// Cíle na tento měsíc (jen owner v měsíčním meetingu) — čte a zapisuje
// /api/settings, tedy STEJNÉ hodnoty jako CEO přehled. Obrat celkem + z nových klientů.
function CileMesice({ mesic }) {
  const { data, refetch } = useApiData(mesic ? `/api/settings?month=${mesic}` : null);
  const [obrat, setObrat] = useState("");
  const [noviObrat, setNoviObrat] = useState("");
  const [ukladam, setUkladam] = useState(false);
  useEffect(() => {
    if (!data) return;
    setObrat(data.monthlyGoal > 0 ? String(data.monthlyGoal) : "");
    setNoviObrat(data.newClientGoal > 0 ? String(data.newClientGoal) : "");
  }, [data]);
  const uloz = async () => {
    setUkladam(true);
    try {
      const r = await apiFetch("/api/settings", { method: "POST", body: JSON.stringify({
        month: mesic,
        monthlyGoal: Number(obrat) || 0,
        newClientGoal: Number(noviObrat) || 0,
      }) });
      if (!r.ok) throw new Error("Uložení selhalo");
      toast(`Cíle na ${fmtMesicCz(mesic)} uloženy, propsané i do CEO přehledu.`, "success");
      refetch();
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
    finally { setUkladam(false); }
  };
  return (
    <div className="rounded-xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white shadow-sm p-4">
      <div className="flex items-center gap-2.5 mb-3 flex-wrap">
        <span className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0"><BarChart3 className="w-5 h-5" /></span>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold text-slate-900">Cíle na tento měsíc</h3>
          <div className="mt-0.5"><span className="inline-flex items-center text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{fmtMesicCz(mesic)}<Delitko />stejné hodnoty jako v CEO přehledu</span></div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Cílový obrat celkem (Kč)</label>
          <input type="number" className="field mt-1 font-semibold" placeholder="např. 500000"
            value={obrat} onChange={(e) => setObrat(e.target.value)} />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-slate-500 whitespace-nowrap">Cílový obrat NEW DEAL (Kč)</label>
          <input type="number" className="field mt-1 font-semibold" placeholder="např. 100000"
            value={noviObrat} onChange={(e) => setNoviObrat(e.target.value)} />
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={uloz} disabled={ukladam} className="btn btn-primary">
          <CheckCircle2 className="w-4 h-4" /> {ukladam ? "Ukládám…" : "Uložit cíle"}
        </button>
        {data?.previousMonth && (
          <span className="text-xs text-slate-400">
            minulý měsíc: {fmtKcG(data.previousMonth.monthlyGoal)} / noví {fmtKcG(data.previousMonth.newClientGoal)}
          </span>
        )}
      </div>
    </div>
  );
}

// Dialog žádosti o odklad úkolu: důvod + čemu se bude pilot věnovat místo toho.
// Owner žádost uvidí v notifikacích a ve Slacku a schválí/zamítne.
// Skupina úkolů jednoho pilota — sbalitelná (šipka), ať je meeting přehledný
// i s více lidmi a úkoly. Výchozí stav: rozbalená.
const inicialy = (jmeno) => String(jmeno).split(/\s+/).map((c) => c[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
function PilotSkupina({ jmeno, pocet, extra = null, defaultOpen = true, hromadny = null, children }) {
  const [open, setOpen] = useState(defaultOpen);
  // Hromadné sbalení/rozbalení všech pilotů najednou — signál z rodiče (tick + cílový stav).
  useEffect(() => { if (hromadny && hromadny.tick > 0) setOpen(hromadny.open); }, [hromadny?.tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const bez = jmeno === "Bez pilota";
  return (
    <div className="mb-1.5 last:mb-0">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-1 pt-1.5 pb-1 rounded-xl hover:bg-slate-50 transition-colors group/pilot"
        title={open ? "Sbalit úkoly pilota" : "Rozbalit úkoly pilota"}>
        <span className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center shrink-0 ${bez ? "bg-slate-100 text-slate-400" : "bg-brand-100 text-brand-700"}`}>{bez ? "?" : inicialy(jmeno)}</span>
        <span className={`text-sm font-bold demo-sensitive ${bez ? "text-slate-400" : "text-slate-700"}`}>{jmeno}</span>
        <span className="text-[11px] font-semibold text-slate-400 tabular-nums">({pocet})</span>
        {extra}
        <span className="flex-1 border-t border-slate-100" />
        <ChevronDown className={`w-4 h-4 text-slate-300 group-hover/pilot:text-slate-500 transition-transform shrink-0 ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="divide-y divide-slate-50">{children}</div>}
    </div>
  );
}

// Projekt v meetingu (6.8.2026): ukoly navazane na projekt se sbali pod dropdown
// s nazvem projektu — defaultne ZAVRENY (prani ownera). V hlavicce je postup
// CELEHO projektu (i ukoly mimo tento meeting) a tlacitko Hotovo, ktere jde
// zmacknout teprve az jsou hotove vsechny jeho ukoly (backend hlida totez).
function ProjektSkupina({ projekt, pocet, hromadny = null, onOpen, onHotovo, children }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (hromadny && hromadny.tick > 0) setOpen(hromadny.open); }, [hromadny?.tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = projekt.deti_total || 0;
  const done = projekt.deti_done || 0;
  const vseHotovo = total > 0 && done === total;
  const uzavreny = ["hotovo", "archiv"].includes(projekt.status);
  return (
    <div className="mb-1.5 last:mb-0 rounded-xl border border-indigo-100 bg-indigo-50/40 overflow-hidden">
      <div className="flex items-center gap-1 px-1.5 py-1.5">
        <button type="button" onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 min-w-0 flex-1 text-left rounded-xl px-1 py-0.5 hover:bg-white/70 transition-colors"
          title={open ? "Sbalit úkoly projektu" : "Rozbalit úkoly projektu"}>
          <FolderKanban className="w-4 h-4 text-indigo-600 shrink-0" />
          <span className="text-sm font-bold text-indigo-900 truncate demo-sensitive">{projekt.nazev}</span>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-white/80 text-indigo-700 border border-indigo-200 shrink-0">
            projekt
            <span aria-hidden className="w-px h-3 bg-indigo-700 opacity-40" />
            {pocet} v meetingu
            <span aria-hidden className="w-px h-3 bg-indigo-700 opacity-40" />
            {done}/{total} hotovo
          </span>
          <ChevronDown className={`w-4 h-4 text-indigo-300 transition-transform shrink-0 ${open ? "" : "-rotate-90"}`} />
        </button>
        <button type="button" onClick={onOpen} title="Otevřít detail projektu"
          className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 px-2 py-1 rounded-xl hover:bg-white/70 shrink-0 min-h-[44px] sm:min-h-0">Detail</button>
        {!uzavreny && (
          <button type="button" disabled={!vseHotovo} onClick={onHotovo}
            title={vseHotovo ? "Označit projekt jako hotový" : `Projekt jde uzavřít až po dokončení všech úkolů (zbývá ${Math.max(0, total - done)}).`}
            className={`text-[11px] font-bold rounded-xl px-2 py-1 border shrink-0 transition-colors ${vseHotovo ? "text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100" : "text-slate-400 bg-slate-50 border-slate-200 cursor-not-allowed"}`}>
            Hotovo
          </button>
        )}
      </div>
      {open && <div className="px-2 pb-1 bg-white/60 divide-y divide-indigo-100/70">{children}</div>}
    </div>
  );
}

function OdkladDialog({ ukol, onClose, onSubmit }) {
  const [duvod, setDuvod] = useState("");
  const [misto, setMisto] = useState("");
  const [posilam, setPosilam] = useState(false);
  // 10.9.2026: portál do document.body. Dialog se renderuje uvnitř KartaSeznam,
  // která má .glass (backdrop-blur, index.css:457) + overflow-hidden (ui.jsx:400).
  // backdrop-filter dělá z karty containing block pro position:fixed, takže se
  // okno ořezalo na velikost karty a byl vidět jen výřez. Stejné řešení jako
  // TaskDetailModal (Ukoly.jsx:1727) a AddTaskModal (Ukoly.jsx:1876).
  return createPortal(
    <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-[2px] flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-amber-50 to-white border-b border-amber-100 px-5 py-4 flex items-center gap-3">
          <span className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><Hourglass className="w-5 h-5" /></span>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-slate-900">Požádat o odklad</h3>
            <div className="text-xs text-slate-500 truncate">{ukol.nazev}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/80 min-h-[44px] sm:min-h-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Proč potřebuješ úkol odložit? *</label>
            <textarea className="field mt-1 min-h-[60px]" placeholder="např. čekám na podklady od dodavatele" value={duvod} onChange={(e) => setDuvod(e.target.value)} autoFocus />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Čemu budeš místo toho věnovat pozornost? *</label>
            <textarea className="field mt-1 min-h-[60px]" placeholder="např. dotáhnu kampaň kamarádka" value={misto} onChange={(e) => setMisto(e.target.value)} />
          </div>
          <div className="text-[11px] text-slate-400">Žádost dostane owner do notifikací i na Slack. Po schválení se úkol přesune do Odloženo.</div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100 bg-slate-50/60">
          <button onClick={onClose} className={btnGhost}>Zrušit</button>
          <button
            disabled={posilam || !duvod.trim() || !misto.trim()}
            onClick={async () => { setPosilam(true); await onSubmit(duvod.trim(), misto.trim()); setPosilam(false); }}
            className="btn btn-primary">
            {posilam ? "Posílám…" : "Poslat žádost"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Detail meetingu: agenda checklist, priority měsíce, úkoly z meetingu, zápis po sekcích.
function MeetingDetail({ meetingId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");
  const [showAddTask, setShowAddTask] = useState(false);
  const [openUkol, setOpenUkol] = useState(null); // detail úkolu přímo z meetingu
  const [odkladDialog, setOdkladDialog] = useState(null); // úkol, o jehož odklad se žádá
  const [pendingDelete, setPendingDelete] = useState(false);
  const [novyUspech, setNovyUspech] = useState("");
  const [novaPoznamka, setNovaPoznamka] = useState("");
  const [rozbaleneRole, setRozbaleneRole] = useState({}); // Poznámky k meetingu (měsíční): rozbalené role
  const [novyNapad, setNovyNapad] = useState("");         // Nápady pro zlepšení (měsíční)
  const [openHodnoceni, setOpenHodnoceni] = useState(null);  // rozkliknuté hodnocení klienta
  const [openPodnety, setOpenPodnety] = useState(null);      // rozbalený souhrn "k probrání" (21.7.2026)
  const [openMimo, setOpenMimo] = useState(false); // "Hotové mimo plán meetingu" — sbaleno defaultně (přání ownera 29.7.)
  const [openUspechy, setOpenUspechy] = useState(false); // Úspěchy: celá sekce sbalitelná, defaultně zavřená (přání ownera 29.7.)
  const [openTydenniUspechy, setOpenTydenniUspechy] = useState(false); // Picker úspěchů z týdenních meetingů (měsíční): default sbaleno
  const [kanbanHidden, setKanbanHidden] = useState(() => new Set()); // Board rychlý přehled: úkoly po kliknutí hned zmizí (optimisticky)
  const [openZapis, setOpenZapis] = useState({});   // Zápis z meetingu: rozbalené role (dropdown jako Poznámky)
  const [novyZapis, setNovyZapis] = useState({});   // Zápis: rozepsaná nová poznámka per role (klíč = idx sekce)
  const [editZapis, setEditZapis] = useState(null); // Zápis: { idx, i, text }
  // Sbalení/rozbalení pilotů — samostatně PRO KAŽDOU dlaždici úkolů (přání ownera 29.7.).
  const [hromMinuly, setHromMinuly] = useState({ tick: 0, open: false }); // minulý týden: default sbaleno
  const [hromPlan, setHromPlan] = useState({ tick: 0, open: false });
  const [hromOdklad, setHromOdklad] = useState({ tick: 0, open: false });
  // Bod 6: kompaktni kanban prehled boardu (filtr + zaradit na pristi tyden)
  const [kanbanOpen, setKanbanOpen] = useState(false);
  const [kanbanHledej, setKanbanHledej] = useState("");
  // Board jako v PM appce (13.8.2026): sloupce podle STAVU, skryvani sloupcu
  // okem s persistenci. Backlog je defaultne skryty (nejvetsi a nejmene akutni).
  const [skryteSloupce, setSkryteSloupce] = useState(() => {
    try {
      const raw = localStorage.getItem("fw40_meeting_board_skryte_sloupce");
      if (raw) return new Set(JSON.parse(raw));
    } catch (_) { /* default */ }
    return new Set(["zasobnik"]);
  });
  const prepniSloupec = (k) => {
    setSkryteSloupce((s2) => {
      const n = new Set(s2);
      if (n.has(k)) n.delete(k); else n.add(k);
      try { localStorage.setItem("fw40_meeting_board_skryte_sloupce", JSON.stringify([...n])); } catch (_) { /* ok */ }
      return n;
    });
  };
  const zobrazVseSloupce = () => {
    setSkryteSloupce(new Set());
    try { localStorage.setItem("fw40_meeting_board_skryte_sloupce", "[]"); } catch (_) { /* ok */ }
  };
  const [kanbanKat, setKanbanKat] = useState("");
  const [kanbanPilot, setKanbanPilot] = useState("");
  // Inline editace: upravit smí autor svoje, owner všechno (stejně jako mazání).
  const [editUspech, setEditUspech] = useState(null);   // { i, text }
  const [editPoznamka, setEditPoznamka] = useState(null); // { i, text }
  const [tazenaPoznamka, setTazenaPoznamka] = useState(null); // index taháné poznámky
  const [cilPoznamky, setCilPoznamky] = useState(null);     // index řádku, nad kterým se drží
  // Mazani poznamky jde pres potvrzovaci dialog (5.8.2026) — krizek u radku
  // byl matouci, uzivatel si myslel ze zavira upravu, a smazal poznamku.
  const [mazatPoznamku, setMazatPoznamku] = useState(null); // { i, text }
  // Meetingu se účastní owner + manager + CSM; správu meetingu drží owner.
  const mojeJmeno = String(getCurrentUser()?.name || getCurrentUser()?.email || "");
  const jeOwner = String(getCurrentUser()?.role || "").toLowerCase() === "owner";

  // Otevřené company úkoly bez vazby na tento meeting (pro "připojit existující").
  const { data: ukolyData, refetch: refetchUkoly } = useApiData("/api/operations-ukoly");

  const load = useCallback(async () => {
    try {
      const r = await apiFetch(`/api/operations-meetingy?meetingId=${meetingId}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Načtení selhalo");
      setDetail(j);
    } catch (e) { setErr(e.message); }
  }, [meetingId]);
  useEffect(() => { load(); }, [load]);

  // Bod 3 (real-time): meeting se sam obnovuje kazdych ~5s, aby vice lidi videlo zmeny
  // toho druheho. Setri Netlify kredity: obnovuje JEN kdyz je zalozka aktivni a uzivatel
  // prave nepise (fokus mimo input/textarea) — rozdelane psani ma vlastni state, takze
  // se neprepise. ~12 volani/min/otevreny uzivatel.
  useEffect(() => {
    if (!meetingId) return;
    let timer;
    const tick = () => {
      try {
        if (document.visibilityState === "visible") {
          const ae = document.activeElement;
          const pise = ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || ae.isContentEditable);
          if (!pise) load();
        }
      } catch (_) { /* ignore */ }
      timer = setTimeout(tick, 5000);
    };
    timer = setTimeout(tick, 5000);
    return () => clearTimeout(timer);
  }, [meetingId, load]);

  const post = useCallback(async (payload, { reload = true } = {}) => {
    setErr("");
    try {
      const r = await apiFetch("/api/operations-meetingy", { method: "POST", body: JSON.stringify(payload) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      // Po uzavření backend automaticky založil navazující meeting → dej vědět.
      if (j.novyMeetingId) toast(`Meeting uzavřen, založil se navazující „${j.novyMeetingNazev || "meeting"}".`, "success");
      if (reload) await load();
      onChanged();
      return true;
    } catch (e) { setErr(e.message); return false; }
  }, [load, onChanged]);

  const m = detail?.meeting;
  const ukoly = detail?.ukoly || [];
  // Projekty pouzite v meetingu (6.8.2026) — klic je id projektu jako string.
  const projektMap = new Map((detail?.projekty || []).map((p) => [String(p.id), p]));
  // Odloženo = to, co jsme odložili na meetingu. PROPISUJE SE napříč meetingy
  // (backend vrací všechna odložená), dokud to někdo neodstraní z odloženo nebo
  // nenaplánuje na týden. Backlog je jiná věc — nápady na později, které žijí
  // v boardu Operations > Úkoly a do meetingu nepatří (18.8.2026).
  // Do planu patri VSECHNO, co je na meeting navazane a neni odlozene.
  // Odlozeni je PRIZNAK, ne stav: ukol si drzi svuj sloupec v kanbanu
  // (Backlog / Prioritni / Mesicni / Tydenni / V procesu) a jen se presune
  // do sekce Odlozeno (18.8.2026). Sloupec je videt na stitku u radku.
  const ukolyPlan = ukoly.filter((u) => u.odlozeno !== true);
  const ukolyOdklad = detail?.odklad !== undefined ? detail.odklad : ukoly.filter((u) => u.odlozeno === true);
  // Systémová kontrola: naplánované úkoly bez pilota/deadlinu a neprobrané poznámky
  // blokují uzavření meetingu (backend hlídá taky).
  const ukolyBezDetailu = ukolyPlan.filter((u) => !["hotovo", "archiv"].includes(u.status) && (!u.pilot || !u.deadline));
  const poznamky = m?.poznamky || [];
  const poznamkyNeprobrane = poznamky.filter((p) => p.done !== true);
  const blokujeUzavreni = m?.typ === "tydenni" && (ukolyBezDetailu.length > 0 || poznamkyNeprobrane.length > 0);
  const predchozi = detail?.predchozi || null;
  const mesic = useMemo(() => (m?.datum ? m.datum.slice(0, 7) : null), [m?.datum]);
  // Týdenní meeting je ve čtvrtek → úkoly se plánují na okno čtvrtek → čtvrtek (+7 dní).
  const planDo = useMemo(() => {
    if (m?.typ !== "tydenni" || !m?.datum) return null;
    const d = new Date(m.datum + "T00:00:00");
    d.setDate(d.getDate() + 7);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, [m?.typ, m?.datum]);
  // Statistiky minulého týdne: pondělí týdne PŘED týdnem meetingu (po–ne).
  const minulyTydenStart = useMemo(() => {
    if (m?.typ !== "tydenni" || !m?.datum) return null;
    const d = new Date(m.datum + "T12:00:00");
    const den = (d.getDay() + 6) % 7; // 0 = pondělí
    d.setDate(d.getDate() - den - 7);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, [m?.typ, m?.datum]);
  const { data: statsTyden, refetch: refetchStatsTyden, refreshing: refreshingStatsTyden } = useApiData(minulyTydenStart ? `/api/data-weekly?weekStart=${minulyTydenStart}` : null);
  // Měsíční meeting (20.7.2026 v3, dle ownera): meeting M07 hodnotí ČERVENEC
  // (= měsíc z data/názvu meetingu, i když se vyhodnocuje začátkem srpna)
  // a plánuje NÁSLEDUJÍCÍ měsíc (cíle + priority na srpen).
  // Měsíční: hodnocený měsíc z NÁZVU (M07/2026), i když je datum meetingu v srpnu.
  const hodnocenyMesic = useMemo(() => {
    if (m?.typ === "mesicni") { const k = mesicKodZNazvu(m?.nazev); if (k) return k.ym; }
    return mesic;
  }, [m?.typ, m?.nazev, mesic]);
  // Plánovaný (následující) měsíc = hodnocený + 1 (u srpnového meetingu za červenec → srpen).
  const dalsiMesic = useMemo(() => {
    if (!hodnocenyMesic) return null;
    const d = new Date(hodnocenyMesic + "-01T12:00:00");
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, [hodnocenyMesic]);
  const { data: statsMesic, refetch: refetchStatsMesic, refreshing: refreshingStatsMesic } = useApiData(m?.typ === "mesicni" && hodnocenyMesic ? `/api/data-monthly?month=${hodnocenyMesic}` : null);
  // 21.7.2026: SM a SEO do Hodnocení marketingu — stejná data jako CEO marketing report
  // (/marketing). Endpoint vrací rolling okna posledních/předchozích 30 dní.
  const { data: mktReport, refetch: refetchMktReport, refreshing: refreshingMktReport } = useApiData(m?.typ === "mesicni" ? "/api/data-marketing" : null);
  // Kvartální report hodnotí PŘEDCHOZÍ uzavřený kvartál (Q4 report vzniká v lednu).
  const kvartalInfo = useMemo(() => {
    if (m?.typ !== "kvartalni" || !m?.datum) return null;
    const d = new Date(m.datum + "T12:00:00");
    let q = Math.floor(d.getMonth() / 3) - 1;
    let year = d.getFullYear();
    if (q < 0) { q = 3; year -= 1; }
    const months = [q * 3 + 1, q * 3 + 2, q * 3 + 3].map((x) => `${year}-${String(x).padStart(2, "0")}`);
    return { year, q: q + 1, months };
  }, [m?.typ, m?.datum]);
  // Hodnocení roku: v lednu hodnotí předchozí rok, jinak aktuální.
  const rokHodnoceni = useMemo(() => {
    if (m?.typ !== "rocni" || !m?.datum) return null;
    const d = new Date(m.datum + "T12:00:00");
    return d.getMonth() === 0 ? d.getFullYear() - 1 : d.getFullYear();
  }, [m?.typ, m?.datum]);
  const { data: statsYearly } = useApiData(
    kvartalInfo ? `/api/data-yearly?year=${kvartalInfo.year}` : (rokHodnoceni ? `/api/data-yearly?year=${rokHodnoceni}` : null)
  );
  const { data: hrTreneri } = useApiData(m?.typ === "kvartalni" ? "/api/hr-list-people?filter=treneri&status=aktivni" : null);

  const attachable = useMemo(() => {
    const linked = new Set(ukoly.map((u) => u.id));
    return (ukolyData?.tasks || []).filter((t) => !linked.has(t.id) && !["hotovo", "archiv"].includes(t.status));
  }, [ukolyData, ukoly]);
  const kanbanFiltrovane = useMemo(() => {
    const q = kanbanHledej.trim().toLowerCase();
    return (ukolyData?.tasks || []).filter((t) =>
      !["hotovo", "archiv"].includes(t.status) &&
      !kanbanHidden.has(t.id) &&
      // Feedback #61: co uz je naplanovane v TOMTO meetingu, tady neukazovat.
      String(t.meeting_id || "") !== String(meetingId) &&
      (!q || String(t.nazev || "").toLowerCase().includes(q)) &&
      (!kanbanKat || t.kategorie === kanbanKat) &&
      (!kanbanPilot || t.pilot === kanbanPilot)
    );
  }, [ukolyData, kanbanHledej, kanbanKat, kanbanPilot, kanbanHidden]);
  // Lidé a kategorie pro plný formulář úkolu (stejná data jako board).
  const lideJmena = useMemo(() => ((ukolyData?.lide || []).map((l) => l.name).filter(Boolean)), [ukolyData]);
  // Board rychlý přehled: dlaždice podle PILOTA (jedna osoba = jedna dlaždice).
  // Úkoly bez pilota / s neznámým pilotem spadnou do dlaždice „Ostatní" (jen když existují).
  // 5.9.2026 (fáze 4a): ikony ze vzorníku místo emoji, barva z tónu sloupce.
  const KANBAN_STATUSY = [
    ["zasobnik", Lightbulb, "Backlog"],
    ["prioritni", AlertTriangle, "Prioritní"],
    ["mesicni", CalendarDays, "Měsíční"],
    ["tydenni", Pin, "Týdenní"],
    ["v_procesu", Rocket, "V procesu"],
  ];

  const kategorieList = ukolyData?.kategorie || ["Marketing", "Operations", "HR", "Clients Management", "System"];
  const fotoByName = useMemo(() => {
    const mapa = {};
    for (const l of (ukolyData?.lide || [])) if (l.name) mapa[l.name] = l.foto || "";
    return mapa;
  }, [ukolyData]);

  const attachTask = async (taskId, { odklad = false, meetingIdCil = null } = {}) => {
    if (!taskId) return;
    setErr("");
    try {
      const cil = meetingIdCil || meetingId;
      // Odlozeni nemeni sloupec v kanbanu, jen zvedne priznak (18.8.2026).
      const fields = odklad ? { meeting_id: cil, odlozeno: true } : { meeting_id: cil };
      const r = await apiFetch("/api/operations-ukoly", {
        method: "POST",
        body: JSON.stringify({ id: taskId, fields }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Připojení selhalo");
      await load();
      refetchUkoly();
      const t = attachable.find((x) => String(x.id) === String(taskId));
      toast(odklad ? `Úkol „${t?.nazev || ""}" přesunut do Odloženo.` : `Úkol „${t?.nazev || ""}" připojen k meetingu.`, "success");
      return true;
    } catch (e2) { setErr(e2.message); return false; }
  };
  // Přesun úkolu na příští týden = deadline na ČTVRTEK následujícího kalendářního týdne.
  const ctvrtekPristihoTydne = () => {
    const d = new Date();
    const den = d.getDay(); // 0=Ne..6=So
    const doPondeli = ((8 - den) % 7) || 7; // dnů do pondělí příštího týdne
    const ct = new Date(d);
    ct.setDate(d.getDate() + doPondeli + 3); // pondělí + 3 = čtvrtek
    return `${ct.getFullYear()}-${String(ct.getMonth() + 1).padStart(2, "0")}-${String(ct.getDate()).padStart(2, "0")}`;
  };
  const presunNaPristiTyden = async (taskId) => {
    if (!taskId) return;
    setErr("");
    try {
      const deadline = ctvrtekPristihoTydne();
      // Presun do pristiho tydne = ukol se NAVAZE na aktualni meeting (objevi se
      // v sekci Ukoly na pristi tyden) a dostane termin ctvrtek pristiho tydne.
      // Sloupec v boardu se NEMENI (prani Ladi 20.8.2026).
      const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify({ id: taskId, fields: { deadline, meeting_id: meetingId } }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Přesun selhal");
      await load();
      refetchUkoly();
      toast(`Úkol zařazen do plánu na příští týden (termín do ${fmtCz(deadline)}).`, "success");
    } catch (e2) { setErr(e2.message); }
  };

  // === Optimistické ukládání: UI reaguje HNED, API běží na pozadí,
  // při chybě se stav vrátí plným reloadem. ===
  const mojeRole = String(getCurrentUser()?.role || "").toLowerCase();

  // Uložení jedné sekce zápisu (podle indexu) — projeví se okamžitě (úspěchy, odpovědi).
  const ulozSekci = (idx, value) => {
    const sekce = m.sekce.map((x, xi) => (xi === idx ? { ...x, obsah: value } : x));
    setDetail((d) => (d ? { ...d, meeting: { ...d.meeting, sekce } } : d));
    post({ action: "update", id: m.id, fields: { sekce } }, { reload: false }).then((ok) => { if (!ok) load(); });
  };
  // Zápis z meetingu: víc poznámek na roli (jako Poznámky). Sekce obsah = JSON [{t,a}];
  // starý jednotlivý text se načte jako jedna poznámka (zpětná kompatibilita).
  const parseZapisList = (obsah) => {
    const raw = String(obsah || "").trim();
    if (!raw) return [];
    if (raw.startsWith("[")) { try { const j = JSON.parse(raw); if (Array.isArray(j)) return j.map((x) => ({ t: String(x.t || ""), a: String(x.a || "") })).filter((x) => x.t); } catch (_) { /* fallback na text */ } }
    return [{ t: raw, a: "" }];
  };
  const ulozZapisList = (idx, list) => ulozSekci(idx, JSON.stringify(list));
  // Pridani zapisu — sdilene pro tlacitko (mobil) i Enter (desktop).
  const pridejZapis = (idx, list) => {
    const txt = (novyZapis[idx] || "").trim();
    if (!txt) return;
    ulozZapisList(idx, [...list, { t: txt, a: mojeJmeno }]);
    setNovyZapis((r) => ({ ...r, [idx]: "" }));
  };

  // Poznámky: lokální změna hned, pak API (odpověď vrací kanonický stav).
  const setPoznamkyLokalne = (pozn) => setDetail((d) => (d ? { ...d, meeting: { ...d.meeting, poznamky: pozn } } : d));
  const postPoznamka = async (payload, optimisticke) => {
    if (optimisticke) setPoznamkyLokalne(optimisticke);
    setErr("");
    try {
      const r = await apiFetch("/api/operations-meetingy", { method: "POST", body: JSON.stringify(payload) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      if (Array.isArray(j.poznamky)) setPoznamkyLokalne(j.poznamky);
      onChanged();
    } catch (e2) { setErr(e2.message); await load(); }
  };

  // Odložený úkol → zařadit do plánu tohoto týdne: shodí příznak Odloženo
  // a přesune do sloupce Týdenní (v měsíčním meetingu do Měsíční).
  const zaraditDoTydne = async (u) => {
    setErr("");
    // Týdenní meeting zařazuje do sloupce Týdenní, měsíční do sloupce Měsíční.
    const cilovyStatus = m?.typ === "mesicni" ? "mesicni" : "tydenni";
    try {
      const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify({ id: u.id, status: cilovyStatus }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Přesun selhal");
      // Bod 6: pri zarazeni na pristi tyden nastav deadline +7 dni (aby byl do dalsiho meetingu).
      const plusSedm = (() => { const d = new Date(); d.setDate(d.getDate() + 7); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
      const fields = cilovyStatus === "tydenni"
        ? { meeting_id: meetingId, deadline: plusSedm, odlozeno: false }
        : { meeting_id: meetingId, odlozeno: false };
      await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify({ id: u.id, fields }) });
      await load();
      refetchUkoly();
      toast(`Úkol „${u.nazev}" zařazen na ${cilovyStatus === "mesicni" ? "tento měsíc" : "příští týden"}${cilovyStatus === "tydenni" ? ` (deadline do ${plusSedm})` : ""}.`, "success");
      return true;
    } catch (e2) { setErr(e2.message); return false; }
  };
  // Board rychlý přehled: klik reaguje OKAMŽITĚ — úkol hned skryjeme, síť běží na pozadí,
  // při chybě ho vrátíme zpět. (přání ownera 29.7.)
  const kanbanRychle = async (u, fn) => {
    setKanbanHidden((s2) => { const n = new Set(s2); n.add(u.id); return n; });
    const ok = await fn();
    if (!ok) setKanbanHidden((s2) => { const n = new Set(s2); n.delete(u.id); return n; });
  };

  // === Odklad úkolu: CSM/manager požádá (důvod + čemu se bude věnovat),
  // owner schválí (úkol → Odloženo) nebo zamítne. ===
  const pozadatOdklad = async (u, duvod, misto) => {
    try {
      const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify({ action: "odkladZadost", id: u.id, duvod, misto }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Odeslání selhalo");
      setOdkladDialog(null);
      toast("Žádost o odklad poslána ownerovi ke schválení.", "success");
      await load();
      refetchUkoly();
    } catch (e2) { toast(`Chyba: ${e2.message}`, "error"); }
  };
  const odkladRozhodni = async (u, schvalit) => {
    try {
      const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify({ action: schvalit ? "odkladSchvalit" : "odkladZamitnout", id: u.id }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Akce selhala");
      toast(schvalit ? `Odklad schválen, „${u.nazev}" je v Odloženo.` : `Odklad zamítnut, „${u.nazev}" platí dál.`, schvalit ? "success" : "info");
      await load();
      refetchUkoly();
    } catch (e2) { toast(`Chyba: ${e2.message}`, "error"); }
  };
  // Akční prvky odkladu pro řádek úkolu (badge žádosti / schválení / tlačítko žádosti).
  const odkladAkce = (u) => {
    if (u.odlozeno === true || ["hotovo", "archiv", "zasobnik"].includes(u.status)) return null;
    if (u.odklad_zadost) {
      return (
        <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
            title={`${u.odklad_od}: ${u.odklad_zadost}, Místo toho: ${u.odklad_misto}`}><Hourglass className="w-3 h-3" /> žádost o odklad</span>
          {jeOwner && (
            <>
              <button type="button" onClick={() => odkladRozhodni(u, true)} aria-label="Schválit odklad"
                title={`Schválit odklad do Odloženo\nProč: ${u.odklad_zadost}\nMísto toho: ${u.odklad_misto}`}
                className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"><Check className="w-4 h-4" /></button>
              <button type="button" onClick={() => odkladRozhodni(u, false)} aria-label="Zamítnout odklad" title="Zamítnout odklad"
                className="flex h-8 w-8 items-center justify-center rounded-xl bg-rose-500 text-white hover:bg-rose-600"><X className="w-4 h-4" /></button>
            </>
          )}
        </span>
      );
    }
    if (!jeOwner) {
      return (
        <button type="button" onClick={(e) => { e.stopPropagation(); setOdkladDialog(u); }}
          title="Požádat ownera o odklad úkolu (s důvodem)"
          className="shrink-0 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-2 py-1 hover:bg-amber-100 transition-colors">
          Odložit
        </button>
      );
    }
    // Owner: "Příští týden" (posune termín na čtvrtek příštího týdne) + "Odložit" (do Odloženo).
    return (
      <div className="shrink-0 flex items-center gap-1.5">
        <button type="button" onClick={(e) => { e.stopPropagation(); presunNaPristiTyden(u.id); }}
          title="Přesunout na příští týden (termín = čtvrtek následujícího týdne)"
          className="text-[11px] font-bold text-brand-700 bg-brand-50 border border-brand-200 rounded-xl px-2 py-1 hover:bg-brand-100 transition-colors">
          Příští týden
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); attachTask(u.id, { odklad: true }); }}
          title="Přesunout do Odloženo"
          className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-2 py-1 hover:bg-amber-100 transition-colors">
          Odložit
        </button>
      </div>
    );
  };

  // Seskupení úkolů dle pilota (abecedně, „Bez pilota" poslední); uvnitř skupiny
  // podle priority (Vysoká → Nízká) a pak deadlinu (nejbližší nahoře).
  const PRIO_RANK = { High: 0, Medium: 1, Low: 2 };
  const seskupDlePilota = (list) => {
    const skupiny = new Map();
    for (const u of list) {
      const k = String(u.pilot || "").trim() || "Bez pilota";
      if (!skupiny.has(k)) skupiny.set(k, []);
      skupiny.get(k).push(u);
    }
    const jmena = [...skupiny.keys()].sort((a, b) => {
      if (a === "Bez pilota") return 1;
      if (b === "Bez pilota") return -1;
      return a.localeCompare(b, "cs");
    });
    return jmena.map((jmeno) => ({
      jmeno,
      ukoly: skupiny.get(jmeno).sort((a, b) =>
        ((PRIO_RANK[a.priority] ?? 3) - (PRIO_RANK[b.priority] ?? 3))
        || String(a.deadline || "9999").localeCompare(String(b.deadline || "9999"))
        || (a.id - b.id)),
    }));
  };
  const seradUkoly = (list) => [...list].sort((a, b) =>
    ((PRIO_RANK[a.priority] ?? 3) - (PRIO_RANK[b.priority] ?? 3))
    || String(a.deadline || "9999").localeCompare(String(b.deadline || "9999"))
    || (a.id - b.id));
  // Render seznamu úkolů: nejdřív PROJEKTY (dropdown, defaultně zavřený — pod
  // projektem jsou jen jeho úkoly zadané v tomhle meetingu), pak zbytek po pilotech.
  const skupinyUkolu = (list, renderRadek, defaultOpen = true, hromadny = null) => {
    const podProjektem = new Map();
    const zbytek = [];
    for (const u of list) {
      const pid = u.parent_id ? String(u.parent_id) : "";
      if (pid && projektMap.has(pid)) {
        if (!podProjektem.has(pid)) podProjektem.set(pid, []);
        podProjektem.get(pid).push(u);
      } else zbytek.push(u);
    }
    const projektySkupiny = [...podProjektem.entries()]
      .map(([pid, us]) => ({ projekt: projektMap.get(pid), ukoly: seradUkoly(us) }))
      .sort((a, b) => String(a.projekt.nazev).localeCompare(String(b.projekt.nazev), "cs"));
    // Feedback #63 (13.8.2026): projektove dropdowny patri POD cloveka, ktery
    // je mel v planu — pilot projektu, fallback nejcastejsi pilot jeho ukolu.
    const projektyDlePilota = new Map();
    for (const sk of projektySkupiny) {
      let pilot = String(sk.projekt.pilot || "").trim();
      if (!pilot) {
        const cetnost = {};
        for (const u of sk.ukoly) { const p = String(u.pilot || "").trim(); if (p) cetnost[p] = (cetnost[p] || 0) + 1; }
        pilot = (Object.entries(cetnost).sort((a, b) => b[1] - a[1])[0] || [""])[0];
      }
      const k = pilot || "Bez pilota";
      if (!projektyDlePilota.has(k)) projektyDlePilota.set(k, []);
      projektyDlePilota.get(k).push(sk);
    }
    const pilotiSkupiny = seskupDlePilota(zbytek);
    for (const jm of projektyDlePilota.keys()) {
      if (!pilotiSkupiny.some((s2) => s2.jmeno === jm)) pilotiSkupiny.push({ jmeno: jm, ukoly: [] });
    }
    return (
      <>
        {pilotiSkupiny.map((sk) => {
          const projekty = projektyDlePilota.get(sk.jmeno) || [];
          const celkemPocet = sk.ukoly.length + projekty.reduce((a, p) => a + p.ukoly.length, 0);
          return (
            <PilotSkupina key={sk.jmeno} jmeno={sk.jmeno} pocet={celkemPocet} defaultOpen={defaultOpen} hromadny={hromadny}>
              {projekty.map((psk) => (
                <ProjektSkupina key={`proj-${psk.projekt.id}`} projekt={psk.projekt} pocet={psk.ukoly.length} hromadny={hromadny}
                  onOpen={() => setOpenUkol(psk.projekt.id)} onHotovo={() => dokoncitProjekt(psk.projekt)}>
                  {psk.ukoly.map(renderRadek)}
                </ProjektSkupina>
              ))}
              {sk.ukoly.map(renderRadek)}
            </PilotSkupina>
          );
        })}
      </>
    );
  };
  const skupinyUkoluSbalene = (list, renderRadek, hromadny = null) => skupinyUkolu(list, renderRadek, false, hromadny);
  // Tlačítko sbalí/rozbalí piloty POUZE ve své dlaždici (bez šipky). Každá sekce má vlastní signál.
  const tlacitkoSbalit = (h, setter) => (
    <button type="button" onClick={() => setter((x) => ({ tick: x.tick + 1, open: !x.open }))}
      className="inline-flex items-center text-[11px] font-semibold text-slate-500 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl px-2 py-1 shrink-0"
      title={h.open ? "Sbalit úkoly v této sekci" : "Rozbalit úkoly v této sekci"}>
      {h.open ? "Sbalit vše" : "Rozbalit vše"}
    </button>
  );

  // Řádek úkolu s pilulkami (stav, priorita, deadline, pilot) — klik otevře detail.
  // kontrola=true: chybějící pilot/deadline se zvýrazní červenou pilulkou.
  const radekUkolu = (u, kontrola = false, extraAkce = null) => {
    const prio = PRIO_PILL[u.priority];
    const aktivni = !["hotovo", "archiv"].includes(u.status);
    return (
      <div key={u.id} className="flex items-center gap-2 py-2 group hover:bg-slate-50 transition-colors -mx-1 px-1 rounded-xl">
        <span className={`w-2 h-2 rounded-full shrink-0 ${u.status === "hotovo" ? "bg-emerald-500" : (u.odlozeno === true || u.status === "zasobnik") ? "bg-slate-300" : "bg-amber-400"}`} />
        <button type="button" onClick={() => setOpenUkol(u.id)} className="min-w-0 flex-1 text-left">
          <div className={`text-sm font-semibold truncate ${u.status === "hotovo" ? "text-slate-400 line-through" : "text-slate-800"}`}>{u.nazev}</div>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${STATUS_PILL[u.status] || "bg-slate-100 text-slate-600"}`}>{STATUS_LABEL[u.status] || u.status}</span>
            {prio && <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${prio[1]}`}>{prio[0]}</span>}
            {u.deadline
              ? <span className="text-[11px] text-slate-400">do {fmtCz(u.deadline)}</span>
              : (kontrola && aktivni && <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">chybí deadline</span>)}
            {u.pilot
              ? <span className="text-[11px] text-slate-500 demo-sensitive inline-flex items-center"><span aria-hidden className="w-px h-3 bg-current opacity-40 mr-1.5" />{u.pilot}</span>
              : (kontrola && aktivni && <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">chybí pilot</span>)}
          </div>
        </button>
        {odkladAkce(u)}
        {extraAkce}
        <button onClick={() => detachTask(u.id, u.odlozeno === true)}
          title={u.odlozeno === true ? "Odebrat z Odloženo (sloupec v kanbanu zůstává)" : "Odpojit od meetingu"}
          className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-rose-600 shrink-0"><X className="w-4 h-4" /></button>
      </div>
    );
  };

  // Projekt -> hotovo primo z meetingu. Tlacitko je aktivni jen kdyz jsou vsechny
  // navazane ukoly hotove; backend to hlida jeste jednou (409 DETI_OTEVRENE).
  const dokoncitProjekt = async (p) => {
    setErr("");
    try {
      const r = await apiFetch("/api/operations-ukoly", { method: "POST", body: JSON.stringify({ id: p.id, status: "hotovo" }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Uložení selhalo");
      await load();
      refetchUkoly();
      toast(`Projekt „${p.nazev}" je hotový.`, "success");
    } catch (e2) { setErr(e2.message); toast(e2.message, "error"); }
  };

  // Krizek u radku. U odlozeneho ukolu jen shodi priznak Odlozeno — ukol
  // zustane ve svem sloupci v kanbanu i navazany na meeting. U ostatnich
  // odpoji od meetingu jako dosud.
  const detachTask = async (taskId, jeOdlozeny) => {
    try {
      const fields = jeOdlozeny ? { odlozeno: false } : { meeting_id: "" };
      await apiFetch("/api/operations-ukoly", {
        method: "POST",
        body: JSON.stringify({ id: taskId, fields }),
      });
      await load();
      refetchUkoly();
    } catch (_) { /* silent */ }
  };

  if (!m) {
    return (
      <div className="glass mx-auto max-w-5xl p-8 text-center text-sm text-slate-400">
        {err || "Načítám meeting…"}
      </div>
    );
  }

  const typ = TYP_META[m.typ] || { label: m.typ, pill: "bg-slate-100 text-slate-700" };
  const agendaDone = m.agenda.filter((a) => a.done).length;

  // Poznamky per role — sdileny render pro mesicni „Poznamky k meetingu“ i ostatni typy
  // (tydenni/kvartalni/rocni). Jeden zdroj pravdy, aby se styl nerozesel. Role = sbalovaci
  // sekce (default sbaleno), uvnitr checkbox + autor + edit/delete, add-input jen pro tvou roli.
  // Ulozeni upravene poznamky (5.8.2026) — sdilene pro Cmd/Ctrl+Enter i tlacitko „Uložit“.
  const ulozPoznamkuEdit = (i) => {
    const text = (editPoznamka?.text || "").trim();
    if (!text) { setEditPoznamka(null); return; }
    postPoznamka({ action: "poznamkaEdit", id: m.id, index: i, text },
      poznamky.map((x, xi) => (xi === i ? { ...x, text } : x)));
    setEditPoznamka(null);
  };
  // Odeslani nove poznamky — sdilene pro tlacitko (mobil) i Enter (desktop).
  const odesliNovouPoznamku = () => {
    const text = novaPoznamka.trim();
    if (!text) return;
    postPoznamka({ action: "poznamkaAdd", id: m.id, text },
      [...poznamky, { text, autor: mojeJmeno, role: mojeRole, done: false }]);
    setNovaPoznamka("");
  };

  // 3.9.2026: pořadí poznámek jde změnit přetažením, stejně jako u záložek nahoře.
  // Odškrtnuté zůstávají nahoře (feedback #65), ruční pořadí platí uvnitř skupiny.
  const presunPoznamku = (odkud, kam) => {
    if (odkud === kam || odkud == null || kam == null) return;
    const nove = poznamky.slice();
    const [tazena] = nove.splice(odkud, 1);
    nove.splice(kam, 0, tazena);
    postPoznamka({ action: "poznamkaPresun", id: m.id, odkud, kam }, nove);
  };

  const renderPoznamkyPerRole = () => (
          <div className="space-y-2">
            {[["owner", "CEO", { radek: "from-brand-100/70 via-brand-50 to-white border-brand-200", pill: "bg-white/80 text-brand-700", hlava: "bg-brand-100 text-brand-700" }],
              ["csm", "CSM", { radek: "from-emerald-100/70 via-emerald-50 to-white border-emerald-200", pill: "bg-white/80 text-emerald-700", hlava: "bg-emerald-100 text-emerald-700" }],
              ["manager", "Manager", { radek: "from-sky-100/70 via-sky-50 to-white border-sky-200", pill: "bg-white/80 text-sky-700", hlava: "bg-sky-100 text-sky-700" }]].map(([role, label, rz]) => {
              const moje = role === mojeRole;
              // Feedback #65 (13.8.2026): probrane (odskrtnute) poznamky nahoru.
              const radkyRole = poznamky.map((p, i) => ({ p, i })).filter(({ p }) => p.role === role)
                .sort((a, b) => ((b.p.done === true) ? 1 : 0) - ((a.p.done === true) ? 1 : 0));
              const otevreno = rozbaleneRole[role] === true; // default SBALENO (přání ownera 20.7.)
              return (
                <div key={role} className="border border-slate-100 rounded-xl overflow-hidden">
                  <button type="button" onClick={() => setRozbaleneRole((r) => ({ ...r, [role]: !otevreno }))}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${rz.hlava}`}>{label}</span>
                    <span className="text-[11px] font-semibold text-slate-400 tabular-nums">({radkyRole.length})</span>
                    <span className="flex-1 border-t border-slate-100" />
                    <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform ${otevreno ? "" : "-rotate-90"}`} />
                  </button>
                  {otevreno && (
                    <div className="px-3 pb-3 space-y-2">
                      {radkyRole.length === 0 && !moje && <div className="text-sm text-slate-400">Zatím nic.</div>}
                      {radkyRole.map(({ p, i }) => (
                        editPoznamka?.i === i ? (
                          // Rezim upravy (5.8.2026): cely radek se promeni v editor pres celou
                          // sirku — text se lame pod sebe misto jizdy do strany. Zamerne tu
                          // NENI krizek ani mazani, uzivatel si ho pletl se zavrenim upravy
                          // a smazal poznamku omylem.
                          <div key={i} className="rounded-xl border-2 border-brand-300 bg-white shadow-sm px-3 py-3">
                            <div className="flex items-center gap-2 mb-2">
                              <Pencil className="w-3.5 h-3.5 text-brand-600" />
                              <span className="text-xs font-bold text-brand-700">Úprava poznámky</span>
                              <Delitko />
                              <span className="text-xs font-semibold text-slate-400 demo-sensitive">{p.autor || "—"}</span>
                            </div>
                            <AutoTextarea autoFocus minHeight={140} maxHeight={560}
                              className="w-full text-sm font-medium text-slate-800 bg-white border border-slate-300 rounded-xl px-3 py-2.5 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-brand-300"
                              value={editPoznamka.text}
                              onChange={(e) => setEditPoznamka({ i, text: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); ulozPoznamkuEdit(i); }
                                if (e.key === "Escape") { e.preventDefault(); setEditPoznamka(null); }
                              }} />
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <button type="button" onClick={() => ulozPoznamkuEdit(i)}
                                className="btn btn-primary">Uložit</button>
                              <button type="button" onClick={() => setEditPoznamka(null)}
                                className="btn btn-secondary">Zrušit</button>
                              <span className="text-[11px] text-slate-400">Enter = nový řádek<Delitko />Cmd/Ctrl+Enter = uložit<Delitko />Esc = zrušit</span>
                            </div>
                          </div>
                        ) : (
                        <div key={i}
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(i)); e.dataTransfer.effectAllowed = "move"; setTazenaPoznamka(i); }}
                          onDragOver={(e) => { e.preventDefault(); if (cilPoznamky !== i) setCilPoznamky(i); }}
                          onDragLeave={() => setCilPoznamky((c) => (c === i ? null : c))}
                          onDragEnd={() => { setTazenaPoznamka(null); setCilPoznamky(null); }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const odkud = parseInt(e.dataTransfer.getData("text/plain"), 10);
                            setTazenaPoznamka(null); setCilPoznamky(null);
                            if (!Number.isNaN(odkud)) presunPoznamku(odkud, i);
                          }}
                          title="Pořadí změníš přetažením"
                          className={`group flex flex-wrap sm:flex-nowrap items-start gap-x-3 gap-y-1.5 rounded-xl px-3 py-2.5 bg-gradient-to-r border shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${cilPoznamky === i && tazenaPoznamka !== i ? "ring-2 ring-brand-300 border-brand-300" : ""} ${tazenaPoznamka === i ? "opacity-50" : ""} ${p.done ? "from-slate-50 via-white to-white border-slate-200 opacity-75" : rz.radek}`}>
                          <input type="checkbox" checked={p.done === true}
                            onChange={() => postPoznamka({ action: "poznamkaToggle", id: m.id, index: i },
                              poznamky.map((x, xi) => (xi === i ? { ...x, done: x.done !== true } : x)))}
                            className="w-5 h-5 mt-0.5 rounded-lg border-slate-300 text-emerald-600 focus:ring-emerald-300 cursor-pointer shrink-0" />
                          <span className={`text-sm font-medium order-last sm:order-none w-full sm:w-auto sm:flex-1 min-w-0 whitespace-pre-wrap break-words leading-relaxed ${p.done ? "line-through text-slate-400" : "text-slate-800"}`}><TextSOdkazy text={p.text} /></span>
                          <span className={`text-xs font-bold px-2.5 py-1 mt-0.5 rounded-full border border-white shrink-0 mr-auto sm:mr-0 demo-sensitive ${p.done ? "bg-white/80 text-slate-400" : rz.pill}`}>{p.autor || "—"}</span>
                          {(jeOwner || p.autor === mojeJmeno) && (
                            <button type="button" onClick={() => setEditPoznamka({ i, text: p.text })}
                              className="mt-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 p-0.5 text-slate-400 hover:text-slate-700 shrink-0 transition-opacity" title="Upravit"><Pencil className="w-4 h-4" /></button>
                          )}
                          {(jeOwner || p.autor === mojeJmeno) && (
                            <button type="button" onClick={() => setMazatPoznamku({ i, text: p.text })}
                              className="mt-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 p-0.5 text-slate-300 hover:text-rose-600 shrink-0 transition-opacity" title="Smazat"><Trash2 className="w-4 h-4" /></button>
                          )}
                        </div>
                        )
                      ))}
                      {moje && (
                        <div>
                          <div className="flex items-end gap-2">
                            <AutoTextarea className="field leading-relaxed resize-none"
                              placeholder="Co chceš sdělit týmu?"
                              value={novaPoznamka}
                              onChange={(e) => setNovaPoznamka(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey && !jeDotykoveZarizeni() && novaPoznamka.trim()) {
                                  e.preventDefault();
                                  odesliNovouPoznamku();
                                }
                              }} />
                            <button type="button" onClick={odesliNovouPoznamku} disabled={!novaPoznamka.trim()}
                              className="shrink-0 w-10 h-10 rounded-xl bg-brand-600 text-white flex items-center justify-center hover:bg-brand-700 disabled:opacity-40 transition-colors"
                              title="Odeslat poznámku"><Send className="w-4 h-4" /></button>
                          </div>
                          <div className="text-[11px] text-slate-400 mt-1 hidden sm:block">Enter odešle, Shift+Enter dělá nový řádek. Dlouhou poznámku rozděl na odstavce, čte se pak líp.</div>
                          <div className="text-[11px] text-slate-400 mt-1 sm:hidden">Poznámku odešleš tlačítkem. Enter dělá nový řádek.</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <ConfirmDialog
              open={!!mazatPoznamku}
              variant="danger"
              title="Smazat poznámku?"
              description={<>Poznámka se smaže natrvalo, vrátit to nejde.
                <span className="block mt-2 p-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 whitespace-pre-wrap break-words max-h-40 overflow-auto">{mazatPoznamku?.text}</span></>}
              confirmLabel="Smazat"
              cancelLabel="Zrušit"
              onCancel={() => setMazatPoznamku(null)}
              onConfirm={() => {
                const i = mazatPoznamku?.i;
                if (i == null) return;
                postPoznamka({ action: "poznamkaDelete", id: m.id, index: i },
                  poznamky.filter((_, xi) => xi !== i));
                setMazatPoznamku(null);
              }} />
          </div>
  );

  return (
    <Stranka sirka="bezna" className="pb-16">
      {/* 4.9.2026 (fáze 4a): hlavička detailu je gradientová jako u ostatních
          stránek, akce jsou ikony. Název a datum se dál mění přímo na místě. */}
      <GradientHeader
        title={m.nazev}
        icon={CalendarCheck}
        akce={[
          { klic: "zpet", ikona: ArrowLeft, popis: "Zpět na seznam", onClick: onClose },
          ...(jeOwner ? [m.stav === "otevreno"
            ? {
                klic: "uzavrit",
                ikona: CheckCircle2,
                popis: blokujeUzavreni ? "Nejdřív doplň úkoly (pilot + termín) a odškrtej poznámky" : "Uzavřít meeting",
                onClick: async () => {
                  // Systémová kontrola: bez pilotů/deadlinů a s neprobranými
                  // poznámkami týdenní meeting neuzavřeš (backend hlídá taky).
                  if (m.typ === "tydenni" && ukolyBezDetailu.length > 0) {
                    toast(`Meeting nejde uzavřít: ${ukolyBezDetailu.map((u) => u.nazev).join(", ")}, doplň pilota a deadline.`, "error");
                    return;
                  }
                  if (m.typ === "tydenni" && poznamkyNeprobrane.length > 0) {
                    toast(`Meeting nejde uzavřít: ${poznamkyNeprobrane.length === 1 ? "1 neprobraná poznámka" : poznamkyNeprobrane.length + " neprobrané poznámky"}, odškrtej je v sekci Poznámky.`, "error");
                    return;
                  }
                  // Uzavřený meeting už není co dělat — zpátky na seznam.
                  // reload: false, protože detail hned zavíráme (seznam se
                  // překreslí přes onChanged uvnitř post).
                  const hotovo = await post({ action: "update", id: m.id, fields: { stav: "hotovo" } }, { reload: false });
                  if (hotovo) onClose();
                },
              }
            : { klic: "otevrit", ikona: RotateCcw, popis: "Znovu otevřít", onClick: () => post({ action: "update", id: m.id, fields: { stav: "otevreno" } }) },
          ] : []),
          ...(jeOwner ? [{ klic: "smazat", ikona: Trash2, popis: "Smazat meeting", onClick: () => setPendingDelete(true) }] : []),
        ]}
      />

      <div className="glass flex flex-wrap items-center gap-2 md:gap-3">
        <input className="field min-w-0 flex-1 font-bold disabled:bg-slate-50 disabled:text-slate-500 md:max-w-[420px]" defaultValue={m.nazev} disabled={!jeOwner}
          onBlur={(e) => e.target.value.trim() !== m.nazev && post({ action: "update", id: m.id, fields: { nazev: e.target.value } })} />
        <input type="date" className="field !w-[132px] shrink-0 text-center disabled:bg-slate-50 disabled:text-slate-500 md:!w-[150px]" defaultValue={m.datum} disabled={!jeOwner}
          onBlur={(e) => e.target.value !== m.datum && post({ action: "update", id: m.id, fields: { datum: e.target.value } })} />
        <ObdobiPruh m={m} />
      </div>
      {err && <Hlaska ton="problem" title="Nepovedlo se">{err}</Hlaska>}

      {/* === Pořadí sekcí kopíruje agendu meetingu; agenda první na očích === */}

      {/* Agenda + Priority měsíce vedle sebe */}
      <div className={`grid grid-cols-1 gap-4 items-start ${["tydenni", "mesicni", "kvartalni"].includes(m.typ) ? "lg:grid-cols-2" : ""}`}>
        {/* Agenda — první na očích: číslované kroky, kterými se meeting řídí.
            Aktuální krok = první neodškrtnutý (zvýrazněný), klik odškrtává.
            5.9.2026: karta ze vzorníku, postup je proužek pod lištou. */}
        <KartaSeznam
          ikona={ListChecks}
          title="Agenda meetingu"
          podtitulek="jedeme bod po bodu, hotové odškrtni"
          odznak={`${agendaDone} z ${m.agenda.length}`}
          odznakTon={agendaDone === m.agenda.length && m.agenda.length > 0 ? "zluta" : "bila"}
        >
          <div className="h-1.5 bg-slate-100">
            <div className={`h-full transition-all ${agendaDone === m.agenda.length && m.agenda.length > 0 ? "bg-emerald-500" : "bg-brand"}`}
              style={{ width: `${m.agenda.length ? Math.round((agendaDone / m.agenda.length) * 100) : 0}%` }} />
          </div>
          <div className="divide-y divide-slate-100">
            {(() => {
              const aktualni = m.agenda.findIndex((a) => a.done !== true);
              return m.agenda.map((a, i) => (
                <button key={i} type="button" onClick={() => {
                    const agenda = m.agenda.map((x, xi) => (xi === i ? { ...x, done: x.done !== true } : x));
                    setDetail((d) => (d ? { ...d, meeting: { ...d.meeting, agenda } } : d));
                    post({ action: "toggleAgenda", id: m.id, index: i }, { reload: false }).then((ok) => { if (!ok) load(); });
                  }}
                  className={`flex min-h-[56px] w-full items-center gap-2.5 px-4 py-2 text-left ${i === aktualni ? "bg-brand-50" : ""}`}>
                  <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                    a.done ? "bg-emerald-500 text-white" : i === aktualni ? "border-2 border-brand bg-white text-brand" : "border-2 border-slate-300 bg-white text-slate-400"
                  }`}>
                    {a.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
                  </span>
                  <span className={`min-w-0 flex-1 text-sm leading-snug ${a.done ? "text-slate-400 line-through" : i === aktualni ? "font-bold text-slate-900" : "font-medium text-slate-800"}`}>{a.text}</span>
                  {i === aktualni && <span className="shrink-0 rounded-full bg-brand px-2 py-0.5 text-[11px] font-bold text-white">Teď</span>}
                </button>
              ));
            })()}
          </div>
        </KartaSeznam>

        <div className="space-y-4">
          {m.typ === "tydenni" && mesic && (
            <MesicniPriority mesic={mesic} highlight />
          )}
      {/* 1. Co se nám povedlo minulý týden — plní CSM den před meetingem (rutina).
          Co řádek, to úspěch s hvězdičkou. */}
      {(() => {
        // Karta úspěchů podle typu meetingu (týdenní / měsíční / kvartální).
        const USPECHY_NADPIS = {
          tydenni: "Co se nám povedlo minulý týden?",
          mesicni: "Co se nám minulý měsíc povedlo?",
          kvartalni: "Co se povedlo",
        };
        const hledanyNadpis = USPECHY_NADPIS[m.typ];
        if (!hledanyNadpis) return null;
        const uspechyIdx = m.sekce.findIndex((s) => s.nadpis === hledanyNadpis);
        if (uspechyIdx < 0) return null;
        // Úspěchy s autorem: nový formát JSON [{t, a}], starý = řádky textu (autor
        // neznámý → upraví/smaže jen owner). Upravit smí autor svoje, owner vše.
        const parseUspechy = (obsah) => {
          const raw = String(obsah || "").trim();
          if (!raw) return [];
          if (raw.startsWith("[")) {
            try {
              const j = JSON.parse(raw);
              if (Array.isArray(j)) return j.map((x) => ({ t: String(x.t || ""), a: String(x.a || "") })).filter((x) => x.t);
            } catch (_) { /* spadne na řádky */ }
          }
          return raw.split("\n").map((x) => x.trim()).filter(Boolean).map((t) => ({ t, a: "" }));
        };
        const uspechy = parseUspechy(m.sekce[uspechyIdx].obsah);
        const uloz = (list) => ulozSekci(uspechyIdx, JSON.stringify(list));
        const smiUpravit = (u) => jeOwner || (u.a && u.a === mojeJmeno);
        // Pridani uspechu — sdilene pro tlacitko (mobil) i Enter (desktop).
        const pridejUspech = () => {
          const txt = novyUspech.trim();
          if (!txt) return;
          setNovyUspech("");
          // Bod 3: append-only pres backend (bezpecne pri soubehu vice lidi) + optimisticky UI.
          const nove = [...uspechy, { t: txt, a: mojeJmeno }];
          setDetail((d) => { if (!d) return d; const sk = d.meeting.sekce.map((x, xi) => (xi === uspechyIdx ? { ...x, obsah: JSON.stringify(nove) } : x)); return { ...d, meeting: { ...d.meeting, sekce: sk } }; });
          post({ action: "uspechAppend", id: m.id, nadpis: hledanyNadpis, t: txt, a: mojeJmeno }, { reload: true });
        };
        return (
          <>
          {/* 5.9.2026: karta ze vzorníku. Amber gradient, animace a emoji nahradil
              zlatý tón značky; obsah, práva i ukládání zůstávají stejné. */}
          <KartaSeznam
            ikona={Trophy}
            title={hledanyNadpis.replace("?", "")}
            podtitulek={m.typ === "tydenni" ? "plní CSM den před meetingem" : "co řádek, to jeden úspěch"}
            odznak={uspechy.length || undefined}
            odznakTon="zluta"
            sbalitelne
            otevreno={openUspechy}
            onToggle={() => setOpenUspechy((v) => !v)}
          >
            {uspechy.length === 0 ? (
              <div className="px-4 py-4 text-sm text-slate-400">Zatím žádný zapsaný úspěch, sem s nimi.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {uspechy.map((u, i) => (
                  <div key={`${i}-${u.t}`} className="flex min-h-[56px] items-center gap-2.5 px-4 py-2">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-brand-gold/25">
                      <Trophy className="h-4 w-4 text-brand-950" />
                    </span>
                    {editUspech?.i === i ? (
                      <>
                        <input
                          autoFocus
                          className="field min-w-0 flex-1"
                          value={editUspech.text}
                          onChange={(e) => setEditUspech({ i, text: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && editUspech.text.trim()) {
                              e.preventDefault();
                              uloz(uspechy.map((x, xi) => (xi === i ? { ...x, t: editUspech.text.trim() } : x)));
                              setEditUspech(null);
                            }
                            if (e.key === "Escape") setEditUspech(null);
                          }}
                          onBlur={() => setEditUspech(null)}
                        />
                        {/* onPointerDown preventDefault: neshodí focus inputu (onBlur by editaci zrušil dřív, než dorazí click) */}
                        <button type="button" title="Uložit úpravu" aria-label="Uložit úpravu"
                          onPointerDown={(e) => e.preventDefault()}
                          onClick={() => { if (editUspech.text.trim()) uloz(uspechy.map((x, xi) => (xi === i ? { ...x, t: editUspech.text.trim() } : x))); setEditUspech(null); }}
                          className="btn-ikona"><Check className="h-5 w-5" /></button>
                      </>
                    ) : (
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800">{u.t}</div>
                        {u.a && <div className="mt-1"><Stitek velikost="mala" ton="neutralni" className="demo-sensitive">{u.a}</Stitek></div>}
                      </div>
                    )}
                    {smiUpravit(u) && editUspech?.i !== i && (
                      <button type="button" onClick={() => setEditUspech({ i, text: u.t })} className="btn-ikona" aria-label="Upravit" title="Upravit">
                        <Pencil className="h-5 w-5" />
                      </button>
                    )}
                    {smiUpravit(u) && editUspech?.i !== i && (
                      <button type="button" onClick={() => uloz(uspechy.filter((_, xi) => xi !== i))} className="btn-ikona" aria-label="Odebrat" title="Odebrat">
                        <X className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {(mojeRole === "csm" || jeOwner) ? (
              <div className="flex items-center gap-2 border-t border-slate-100 p-3">
                <input
                  className="field min-w-0 flex-1"
                  placeholder="Přidat úspěch… (např. Prodali jsme 5 permanentek)"
                  value={novyUspech}
                  onChange={(e) => setNovyUspech(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && novyUspech.trim()) { e.preventDefault(); pridejUspech(); } }}
                />
                <button type="button" onClick={pridejUspech} disabled={!novyUspech.trim()}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand text-white disabled:opacity-40"
                  aria-label="Přidat úspěch" title="Přidat úspěch"><Send className="h-5 w-5" /></button>
              </div>
            ) : (
              <div className="border-t border-slate-100 px-4 py-3 text-[11px] text-slate-400">Úspěchy zapisuje CSM (owner může taky, zapíše se jeho jméno).</div>
            )}
          </KartaSeznam>
          {m.typ === "mesicni" && (detail?.tydenniUspechy || []).length > 0 && (() => {
            // Z týdenních meetingů: úspěchy zapsané v týdenních meetinzích hodnoceného
            // měsíce. Owner/CSM z nich vybere max TOP 5 do měsíčních úspěchů. Default sbaleno.
            const skupiny = detail.tydenniUspechy || [];
            const celkem = skupiny.reduce((n, g) => n + (g.uspechy || []).length, 0);
            const mesicniTexty = new Set(uspechy.map((u) => String(u.t || "").trim()));
            const jeVybrany = (t) => mesicniTexty.has(String(t || "").trim());
            const vybranoZTydenu = skupiny.reduce((n, g) => n + (g.uspechy || []).filter((u) => jeVybrany(u.t)).length, 0);
            const LIMIT = 5;
            const plno = vybranoZTydenu >= LIMIT;
            const smiPridat = mojeRole === "csm" || jeOwner;
            const fmtD = (iso) => { if (!iso) return ""; const [y, mm, dd] = String(iso).split("-"); return `${+dd}. ${+mm}. ${y}`; };
            const pridej = (txt) => {
              const t = String(txt || "").trim();
              if (!t || plno || jeVybrany(t)) return;
              const nove = [...uspechy, { t, a: mojeJmeno }];
              setDetail((d) => { if (!d) return d; const sk = d.meeting.sekce.map((x, xi) => (xi === uspechyIdx ? { ...x, obsah: JSON.stringify(nove) } : x)); return { ...d, meeting: { ...d.meeting, sekce: sk } }; });
              post({ action: "uspechAppend", id: m.id, nadpis: hledanyNadpis, t, a: mojeJmeno }, { reload: true });
            };
            return (
              <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
                <button type="button" onClick={() => setOpenTydenniUspechy((v) => !v)} className="w-full flex items-center gap-3 text-left">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-slate-900">Z týdenních meetingů</h3>
                    <div className="text-xs text-slate-500 mt-0.5">{celkem} {celkem === 1 ? "zapsaný úspěch" : (celkem >= 2 && celkem <= 4 ? "zapsané úspěchy" : "zapsaných úspěchů")} a radostí za hodnocený měsíc</div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full px-3 py-1.5 shrink-0">
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openTydenniUspechy ? "" : "-rotate-90"}`} />
                    {openTydenniUspechy ? "Skrýt" : "Zobrazit"}
                  </span>
                </button>
                {openTydenniUspechy && (
                  <div className="mt-3 space-y-4">
                    {smiPridat && (
                      <div className={`text-xs font-semibold ${plno ? "text-amber-600" : "text-slate-400"}`}>Vybráno {vybranoZTydenu}/{LIMIT}{plno ? ", dosažen maximální počet TOP úspěchů" : ""}</div>
                    )}
                    {skupiny.map((g) => (
                      <div key={g.meetingId}>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Týdenní meeting<Delitko />{fmtD(g.datum)}</div>
                        <div className="space-y-1.5">
                          {(g.uspechy || []).map((u, i) => {
                            const vyb = jeVybrany(u.t);
                            return (
                              <div key={`${g.meetingId}-${i}`} className={`flex items-center gap-2.5 rounded-xl px-3 py-2 border ${vyb ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-slate-50"}`}>
                                <span className="text-sm text-slate-800 flex-1 min-w-0">{u.t}</span>
                                {u.a && <span className="text-[11px] font-semibold text-slate-400 bg-white border border-slate-200 rounded-full px-2 py-0.5 shrink-0 demo-sensitive">{u.a}</span>}
                                {smiPridat && (
                                  vyb
                                    ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 shrink-0"><CheckCircle2 className="w-3.5 h-3.5" /> vybráno</span>
                                    : <button type="button" onClick={() => pridej(u.t)} disabled={plno}
                                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl px-2 py-1 shrink-0" title={plno ? "Maximálně 5 úspěchů" : "Přidat do úspěchů měsíce"}>
                                        <Plus className="w-3.5 h-3.5" /> Vybrat
                                      </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          </>
        );
      })()}

        </div>
      </div>

      {/* Poznámky k meetingu (měsíční, 20.7.2026): před informace týmu per role.
          Každý si je připraví PŘED meetingem (rutina, deadline 1. den v měsíci),
          na meetingu se odškrtávají. Role = rozbalovací sekce, styl jako poznámky. */}
      {m.typ === "mesicni" && (
        <KartaSeznam
          ikona={StickyNote}
          title="Poznámky k meetingu"
          podtitulek="každý si připraví před meetingem, na meetingu se probere a odškrtne"
          odznak={poznamky.length > 0 ? `${poznamky.length - poznamkyNeprobrane.length} z ${poznamky.length}` : undefined}
          odznakTon={poznamky.length > 0 && poznamkyNeprobrane.length === 0 ? "zluta" : "bila"}
        >
          <div className="p-3 sm:p-4">{renderPoznamkyPerRole()}</div>
        </KartaSeznam>
      )}

      {/* 2. Statistika za poslední týden (jen týdenní meeting) */}
      {m.typ === "tydenni" && <StatsMinulyTyden data={statsTyden} weekStart={minulyTydenStart} refetch={refetchStatsTyden} refreshing={refreshingStatsTyden} />}

      {/* === Měsíční meeting (v3): meeting M07 HODNOTÍ červenec (statistiky,
           priority s plněním) a PLÁNUJE srpen (cíle + priority). === */}
      {m.typ === "mesicni" && (
        <StatsMesic data={statsMesic} mesic={hodnocenyMesic}
          refetch={() => { refetchStatsMesic(); refetchMktReport(); }}
          refreshing={refreshingStatsMesic || refreshingMktReport} />
      )}
      {m.typ === "mesicni" && mesic && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <div>
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="w-2 h-2 rounded-full bg-slate-400" />
              <span className="text-sm font-bold text-slate-700">Hodnocení měsíce</span>
              <span className="text-xs font-bold text-slate-400">{fmtMesicCz(hodnocenyMesic)}</span>
            </div>
            <div className="space-y-4">
              {/* Plnění finančních cílů hodnoceného měsíce (zrcadlí Cíle z plánu vpravo) */}
              {statsMesic && (statsMesic.monthlyGoal > 0 || statsMesic.newClientGoal > 0) && (
                <KartaSeznam
                  ikona={BarChart3}
                  title="Plnění cílů"
                  podtitulek={`${fmtMesicCz(hodnocenyMesic)}, stejné hodnoty jako v CEO přehledu`}
                >
                <div className="space-y-3 p-3 sm:p-4">
                  {[
                    { label: "Obrat celkem", cil: statsMesic.monthlyGoal, splneno: statsMesic.totalInvoicesRevenue, pct: statsMesic.monthlyGoalPct },
                    { label: "Obrat NEW DEAL", cil: statsMesic.newClientGoal, splneno: statsMesic.newClientRevenue, pct: statsMesic.newClientGoalPct },
                  ].filter((c) => c.cil > 0).map((c) => (
                    <div key={c.label}>
                      <div className="flex items-center gap-2 text-sm mb-1">
                        <span className="font-semibold text-slate-700 flex-1">{c.label}</span>
                        <span className="tabular-nums text-slate-500">{fmtKcG(c.splneno)} / {fmtKcG(c.cil)}</span>
                        <span className={`font-bold tabular-nums ${c.pct >= 100 ? "text-emerald-600" : c.pct >= 70 ? "text-amber-600" : "text-rose-600"}`}>{c.pct} %</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                        <div className={`h-full rounded-full transition-all ${c.pct >= 100 ? "bg-emerald-500" : c.pct >= 70 ? "bg-amber-400" : "bg-rose-400"}`}
                          style={{ width: `${Math.min(c.pct || 0, 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                </KartaSeznam>
              )}
              <MesicniPriority mesic={hodnocenyMesic} />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-sm font-bold text-slate-700">Plán na následující měsíc</span>
              <span className="text-xs font-bold text-slate-400">{fmtMesicCz(dalsiMesic)}</span>
            </div>
            <div className="space-y-4">
              {jeOwner && <CileMesice mesic={dalsiMesic} />}
              <MesicniPriority mesic={dalsiMesic} highlight />
            </div>
          </div>
        </div>
      )}

      {/* === Kvartální report / hodnocení roku: statistiky z appky === */}
      {m.typ === "kvartalni" && <StatsKvartal yearly={statsYearly} info={kvartalInfo} treneri={hrTreneri} />}
      {m.typ === "rocni" && <StatsRok yearly={statsYearly} rok={rokHodnoceni} />}

      {/* Bod 6: Kompaktni prehled boardu — filtruj a jednim klikem zarad ukol na pristi tyden */}
      {m.typ === "tydenni" && (
        <KartaSeznam
          ikona={ListChecks}
          title="Board"
          podtitulek="rychlý přehled: vyber úkol a zařaď ho na příští týden"
          sbalitelne
          otevreno={kanbanOpen}
          onToggle={() => setKanbanOpen((v) => !v)}
        >
          {kanbanOpen && (
            <div className="p-3 sm:p-4">
              <div className="flex flex-wrap gap-2 mb-3">
                <input value={kanbanHledej} onChange={(e) => setKanbanHledej(e.target.value)} placeholder="Hledat nazev..." className="field flex-1 min-w-[140px]" />
                <select value={kanbanKat} onChange={(e) => setKanbanKat(e.target.value)} className="field w-auto">
                  <option value="">Vsechny kategorie</option>
                  {kategorieList.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <select value={kanbanPilot} onChange={(e) => setKanbanPilot(e.target.value)} className="field w-auto">
                  <option value="">Vsichni piloti</option>
                  {lideJmena.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              {(() => {
                const skryte = KANBAN_STATUSY.filter(([k]) => skryteSloupce.has(k));
                if (!skryte.length) return null;
                return (
                  <div className="flex items-center gap-1.5 flex-wrap mb-2 px-1">
                    <span className="text-[11px] font-semibold text-slate-400">Skryté sloupce:</span>
                    {skryte.map(([k, IkonaSloupce, label]) => (
                      <button key={k} type="button" onClick={() => prepniSloupec(k)} title="Zobrazit sloupec zpět"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-full px-2 py-0.5">
                        <Eye className="w-3 h-3" /> <IkonaSloupce className="w-3 h-3" /> {label} <span className="tabular-nums">{kanbanFiltrovane.filter((t) => t.status === k).length}</span>
                      </button>
                    ))}
                    <button type="button" onClick={zobrazVseSloupce} className="text-[11px] font-bold text-brand-700 hover:text-brand-900 px-1.5">Zobrazit vše</button>
                  </div>
                );
              })()}
              {/* Sloupce vyplni celou sirku — pri skrytych sloupcich se zbyle
                  roztahnou, board neni zmackly vlevo (13.8.2026, Láďa). */}
              <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))' }}>
                {KANBAN_STATUSY.filter(([k]) => !skryteSloupce.has(k)).map(([k, IkonaSloupce, label]) => {
                  const items = kanbanFiltrovane.filter((t) => t.status === k);
                  return (
                    <div key={k} className="bg-slate-50 border border-slate-100 rounded-xl p-2">
                      <div className="flex items-center gap-1.5 mb-1.5 px-1">
                        <IkonaSloupce className={`w-4 h-4 shrink-0 ${tonTypu(TON_STATUS[k]).text}`} />
                        <span className="text-sm font-bold text-slate-700 truncate">{label}</span>
                        <span className="text-[11px] font-bold text-slate-500 bg-white border border-slate-200 rounded-full px-1.5 py-0.5 tabular-nums">{items.length}</span>
                        <button type="button" onClick={() => prepniSloupec(k)}
                          title="Skrýt sloupec (vrátíš ho řádkem Skryté sloupce nahoře)"
                          className="ml-auto p-1 text-slate-300 hover:text-slate-600 shrink-0">
                          <EyeOff className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="space-y-1 max-h-[32rem] overflow-y-auto">
                        {items.length === 0 ? (
                          <div className="text-[11px] text-slate-300 px-1 py-1">Prázdné.</div>
                        ) : items.map((u) => {
                          // Projekt sdružuje další úkoly — na kartě to musí být vidět hned,
                          // stejně jako na velkém boardu (proužek, štítek, kolik je hotovo).
                          const jeProjekt = u.je_projekt === true || (u.deti_total || 0) > 0;
                          const projCelkem = u.deti_total || 0;
                          const projHotovo = u.deti_done || 0;
                          const projPct = projCelkem > 0 ? Math.round((projHotovo / projCelkem) * 100) : 0;
                          return (
                          <div key={u.id} className={`bg-white border rounded-xl overflow-hidden ${jeProjekt ? "border-indigo-200" : "border-slate-100"}`}>
                           <div className="flex">
                            <div className={`w-1.5 shrink-0 ${jeProjekt ? "bg-indigo-500" : "bg-transparent"}`} aria-hidden="true" />
                            <div className="min-w-0 flex-1 px-2 py-1.5">
                            <button type="button" onClick={() => setOpenUkol(u.id)} title="Otevřít detail úkolu" className="w-full min-w-0 text-left">
                              {jeProjekt && (
                                <span className="inline-flex items-center gap-1 mb-1 text-[11px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-indigo-600 text-white">
                                  <FolderKanban className="w-3 h-3" /> Projekt
                                </span>
                              )}
                              <div className="text-xs font-semibold text-slate-800">{u.nazev}</div>
                              {projCelkem > 0 && (
                                <div className="mt-1 flex items-center gap-1.5 text-[11px] font-bold text-indigo-700">
                                  {projHotovo}/{projCelkem} úkolů
                                  <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-indigo-100">
                                    <span className="block h-full bg-indigo-500" style={{ width: `${projPct}%` }} />
                                  </span>
                                </div>
                              )}
                            </button>
                            <div className="flex items-center gap-1 flex-wrap mt-1">
                              {u.pilot && <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-brand-50 text-brand-700 border border-brand-100 demo-sensitive">{u.pilot}</span>}
                              {PRIO_PILL[u.priority] && <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${PRIO_PILL[u.priority][1]}`}>{PRIO_PILL[u.priority][0]}</span>}
                              {u.deadline && <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-100 tabular-nums">do {fmtCz(u.deadline)}</span>}
                              <span className="flex-1" />
                              {u.status !== "tydenni" ? (
                                <button type="button" onClick={() => kanbanRychle(u, () => zaraditDoTydne(u))} title="Přesunout na příští týden, deadline se nastaví +7 dní (do dalšího meetingu)" className="btn btn-primary shrink-0 whitespace-nowrap">Do týdne</button>
                              ) : (
                                <button type="button" onClick={() => kanbanRychle(u, () => attachTask(u.id, {}))} title="Přidat tento úkol do meetingu (do plánu tohoto týdne)" className="shrink-0 text-[11px] font-bold text-brand-700 bg-brand-50 border border-brand-200 hover:bg-brand-100 rounded-xl px-2.5 py-1 transition-colors whitespace-nowrap">Do meetingu</button>
                              )}
                            </div>
                            </div>
                           </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </KartaSeznam>
      )}
      {/* Úkoly z minulého meetingu — samostatně, s aktuálním stavem z boardu.
          MĚSÍČNÍ je neřeší (věc týdenního) — místo nich má Hotové úkoly za měsíc. */}
      <div className="grid grid-cols-1 gap-4">
        {/* 4. Úkoly z minulého týdne — u týdenního meetingu vždy, i když je prázdný */}
        {(m.typ === "tydenni" || (m.typ !== "mesicni" && predchozi && predchozi.ukoly.length > 0)) && (
          <KartaSeznam
            ikona={RotateCcw}
            title={m.typ === "tydenni" ? "Úkoly z minulého týdne" : m.typ === "mesicni" ? "Úkoly z minulého měsíce" : "Úkoly z minulého meetingu"}
            podtitulek={predchozi?.meeting ? `${predchozi.meeting.nazev}, ${fmtCz(predchozi.meeting.datum)}` : undefined}
          >
            <div className="p-3 sm:p-4">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              {(predchozi?.ukoly || []).length > 0 && (() => {
                const done = predchozi.ukoly.filter((u) => u.status === "hotovo" || u.status === "archiv").length;
                const vse = done === predchozi.ukoly.length;
                return (
                  <span className={`text-xs font-bold tabular-nums px-2 py-0.5 rounded-full ${vse ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {done}/{predchozi.ukoly.length} hotovo
                  </span>
                );
              })()}
              {(predchozi?.ukoly || []).length > 0 && tlacitkoSbalit(hromMinuly, setHromMinuly)}
              {predchozi?.meeting && (
                <Link to={`/operations/meetingy?open=${predchozi.meeting.id}`} className="text-xs font-semibold text-brand-700 hover:text-brand-900">Otevřít</Link>
              )}
            </div>
            {(predchozi?.ukoly || []).length === 0 && !(m.typ === "tydenni" && (detail?.mimoMeeting || []).length > 0) ? (
              <div className="text-sm text-slate-400 py-1">{m.typ === "mesicni" ? "Žádné úkoly z minulého měsíce." : "Žádné úkoly z minulého týdne."}</div>
            ) : (
            <div className="-mx-1">
              {skupinyUkoluSbalene(predchozi?.ukoly || [], (u) => {
                const done = ["hotovo", "archiv"].includes(u.status);
                const prio = PRIO_PILL[u.priority];
                return (
                  <div key={u.id} className="flex items-center gap-2.5 py-2 px-1 hover:bg-slate-50 transition-colors group">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${done ? "bg-emerald-500" : "bg-amber-400"}`} />
                    {/* Vnitřní button (ne button v buttonu — validní HTML kvůli tlačítku odkladu vedle) */}
                    <button type="button" onClick={() => setOpenUkol(u.id)} className="min-w-0 flex-1 text-left">
                      <div className={`text-sm font-semibold truncate ${done ? "text-slate-400 line-through" : "text-slate-800"}`}>{u.nazev}</div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${STATUS_PILL[u.status] || "bg-slate-100 text-slate-600"}`}>{STATUS_LABEL[u.status] || u.status}</span>
                        {prio && <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${prio[1]}`}>{prio[0]}</span>}
                        {u.kategorie && <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-100">{u.kategorie}</span>}
                        {u.deadline && <span className="text-[11px] text-slate-400">do {fmtCz(u.deadline)}</span>}
                        {u.pilot && <span className="text-[11px] text-slate-500 demo-sensitive">, {u.pilot}</span>}
                      </div>
                    </button>
                    {odkladAkce(u)}

                  </div>
                );
              }, hromMinuly)}
            </div>
            )}
            {/* Hotové mimo plán (přesunuto sem 21.7.2026): retrospektiva = plán z minula + co se udělalo mimo něj */}
            {m.typ === "tydenni" && (detail?.mimoMeeting || []).length > 0 && (
              <div className="mt-2">
                <button type="button" onClick={() => setOpenMimo((o) => !o)}
                  className="w-full flex items-center gap-1.5 text-xs font-bold text-slate-500 mb-1 hover:text-slate-700">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Hotové mimo plán meetingu
                  <span className="text-slate-400 tabular-nums">({(detail?.mimoMeeting || []).length})</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition ${openMimo ? "rotate-180" : ""}`} />
                </button>
                {openMimo && (
                <div className="-mx-1">
                  {skupinyUkolu(detail?.mimoMeeting || [], (u) => {
                    const done = ["hotovo", "archiv"].includes(u.status);
                    const prio = PRIO_PILL[u.priority];
                    return (
                      <div key={u.id} className="flex items-center gap-2.5 py-2 px-1 hover:bg-slate-50 transition-colors group">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${done ? "bg-emerald-500" : "bg-amber-400"}`} />
                        <button type="button" onClick={() => setOpenUkol(u.id)} className="min-w-0 flex-1 text-left">
                          <div className={`text-sm font-semibold truncate ${done ? "text-slate-400 line-through" : "text-slate-800"}`}>{u.nazev}</div>
                          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                            <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${STATUS_PILL[u.status] || "bg-slate-100 text-slate-600"}`}>{STATUS_LABEL[u.status] || u.status}</span>
                            {prio && <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full ${prio[1]}`}>{prio[0]}</span>}
                            {u.kategorie && <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-100">{u.kategorie}</span>}
                            {u.hotovo_kdy && <span className="text-[11px] text-slate-400">{fmtCz(u.hotovo_kdy)}</span>}
                          </div>
                        </button>

                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            )}
            {/* Owner může ručně doplnit úkoly, které patřily k minulému týdnu */}
            {jeOwner && predchozi?.meeting && attachable.length > 0 && (
              <div className="mt-2 pt-2 border-t border-slate-100">
                <UkolPicker tasks={attachable} value=""
                  onChange={(id) => attachTask(id, { meetingIdCil: predchozi.meeting.id })}
                  placeholder="Přidat úkol do minulého týdne… (hledej názvem)" className="w-full" />
              </div>
            )}
            </div>
          </KartaSeznam>
        )}
      </div>

      {/* Hotové úkoly za hodnocený měsíc (MĚSÍČNÍ meeting): per pilot s jednoduchou
          statistikou — počet úkolů, hodiny z time trackingu, hlavní činnost. */}
      {m.typ === "mesicni" && (
        <KartaSeznam
          ikona={CheckCircle2}
          title={`Hotové úkoly za ${fmtMesicCz(hodnocenyMesic)}`}
          podtitulek="co kdo dokončil a hodiny z time trackingu"
          odznak={(detail?.hotoveMesic || []).length || undefined}
        >
          <div className="p-3 sm:p-4">
          {(detail?.hotoveMesic || []).length === 0 ? (
            <div className="text-sm text-slate-400">Žádné dokončené úkoly v hodnoceném měsíci.</div>
          ) : (
            seskupDlePilota(detail.hotoveMesic).map((sk) => {
              const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
              // Time tracking záznamy osoby: osoba × činnost × kvadrant → hodiny.
              const zaznamy = (detail?.hodinyMesic || []).filter((h) => {
                const a = norm(h.jmeno), b = norm(sk.jmeno);
                return a && b && (a === b || a.includes(b.split(" ")[0]) || b.includes(a.split(" ")[0]));
              });
              const hodinyCelkem = Math.round(zaznamy.reduce((s, z) => s + (z.hodiny || 0), 0) * 10) / 10;
              const oblasti = Object.entries(zaznamy.reduce((acc, z) => { acc[z.oblast] = (acc[z.oblast] || 0) + (z.hodiny || 0); return acc; }, {}))
                .map(([oblast, h]) => ({ oblast, h: Math.round(h * 10) / 10 })).sort((a, b) => b.h - a.h);
              const kvadranty = Object.entries(zaznamy.reduce((acc, z) => { const q = z.qx || "?"; acc[q] = (acc[q] || 0) + (z.hodiny || 0); return acc; }, {}))
                .map(([q, h]) => ({ q, h })).sort((a, b) => String(a.q).localeCompare(String(b.q)));
              // Splněno po deadlinu (hotovo_kdy > deadline).
              const poDeadlinu = sk.ukoly.filter((u) => u.deadline && u.hotovo_kdy && String(u.hotovo_kdy).slice(0, 10) > String(u.deadline).slice(0, 10)).length;
              const QB = { Q1: "bg-rose-400", Q2: "bg-emerald-500", Q3: "bg-amber-400", Q4: "bg-slate-300" };
              return (
                <PilotSkupina key={sk.jmeno} jmeno={sk.jmeno} pocet={sk.ukoly.length} defaultOpen={false}
                  extra={
                    <span className="flex items-center gap-1.5 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 tabular-nums"><Check className="w-3 h-3" /> {sk.ukoly.length} splněno</span>
                      {poDeadlinu > 0 && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 tabular-nums">{poDeadlinu} po deadlinu</span>}
                      {hodinyCelkem > 0 && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 tabular-nums">{hodinyCelkem} h</span>}
                      {oblasti[0] && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 tabular-nums hidden sm:inline">nejvíc: {oblasti[0].oblast} ({oblasti[0].h} h)</span>}
                      {kvadranty.filter((k) => k.q && k.q !== "?").map((k) => (
                        <span key={k.q} className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-white border border-slate-200 text-slate-500 tabular-nums hidden lg:inline">
                          {k.q} {hodinyCelkem > 0 ? Math.round((k.h / hodinyCelkem) * 100) : 0} %
                        </span>
                      ))}
                    </span>
                  }>
                  {/* Analýza času: činnosti + kvadranty (viditelné po rozbalení) */}
                  {zaznamy.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-1 py-2">
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">Čas dle činností</div>
                        <div className="space-y-1">
                          {oblasti.slice(0, 5).map((o) => (
                            <div key={o.oblast} className="flex items-center gap-2">
                              <span className="text-xs text-slate-600 flex-1 min-w-0 truncate">{o.oblast}</span>
                              <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden shrink-0">
                                <div className="h-full bg-sky-400 rounded-full" style={{ width: `${hodinyCelkem > 0 ? Math.round((o.h / hodinyCelkem) * 100) : 0}%` }} />
                              </div>
                              <span className="text-xs font-semibold text-slate-500 tabular-nums shrink-0 w-12 text-right">{o.h} h</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">Kvadranty</div>
                        <div className="space-y-1">
                          {kvadranty.map((k) => (
                            <div key={k.q} className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-slate-600 w-8 shrink-0">{k.q === "?" ? "—" : k.q}</span>
                              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${QB[k.q] || "bg-slate-300"}`} style={{ width: `${hodinyCelkem > 0 ? Math.round((k.h / hodinyCelkem) * 100) : 0}%` }} />
                              </div>
                              <span className="text-xs font-semibold text-slate-500 tabular-nums shrink-0 w-16 text-right">{Math.round(k.h * 10) / 10} h, {hodinyCelkem > 0 ? Math.round((k.h / hodinyCelkem) * 100) : 0} %</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 px-1 pt-1">Dokončené úkoly</div>
                  {sk.ukoly.map((u) => {
                    const pozde = u.deadline && u.hotovo_kdy && String(u.hotovo_kdy).slice(0, 10) > String(u.deadline).slice(0, 10);
                    return (
                      <button key={u.id} type="button" onClick={() => setOpenUkol(u.id)}
                        className="w-full flex items-center gap-2 py-1.5 px-1 text-left hover:bg-emerald-50/50 transition-colors rounded-xl">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${pozde ? "bg-rose-400" : "bg-emerald-500"}`} />
                        <span className="text-sm font-medium text-slate-500 line-through truncate flex-1 min-w-0">{u.nazev}</span>
                        {pozde && <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-600 shrink-0">po deadlinu</span>}
                        <span className="text-[11px] text-slate-400 shrink-0">{u.hotovo_kdy ? fmtCz(u.hotovo_kdy) : ""}</span>
                      </button>
                    );
                  })}
                </PilotSkupina>
              );
            })
          )}
          </div>
        </KartaSeznam>
      )}

      {/* Hodnocení marketingu (z dat, co už máme) + Hodnocení od klientů (klient_hodnoceni)
          — obě sekce jen v MĚSÍČNÍM meetingu, počítají se automaticky. */}
      {m.typ === "mesicni" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {/* 7.8.2026: dva samostatné sloupce místo 4 položek v gridu — dlaždice se
              tak skládají těsně na sebe (nutriční konzultace hned pod hodnocením
              od klientů), ne na společný řádek s mezerou. */}
          <div className="space-y-4">
          <KartaSeznam
            ikona={BarChart3}
            title="Hodnocení marketingu"
            podtitulek={`${fmtMesicCz(hodnocenyMesic)}, počítá se automaticky z dat`}
          >
            <div className="p-3 sm:p-4">
            {!statsMesic ? (
              <div className="text-sm text-slate-400">Načítám…</div>
            ) : (
              <div className="divide-y divide-slate-50">
                {[
                  { l: "Útrata za reklamu", v: fmtKcG(statsMesic.mktSpend) },
                  { l: "Leady", v: `${statsMesic.leads ?? 0}`, s: `${statsMesic.nqLeads || 0} NQ, ${(statsMesic.leads || 0) > 0 ? Math.round(((statsMesic.nqLeads || 0) / statsMesic.leads) * 100) : 0} % NQ` },
                  { l: "CPL (cena za lead)", v: fmtKcG(statsMesic.cpl) },
                  { l: "Trialy realizované", v: `${statsMesic.realizedTrialCount ?? 0}`, s: (statsMesic.realizedTrialCount > 0 && statsMesic.mktSpend > 0) ? `cost/trial ${fmtKcG(Math.round(statsMesic.mktSpend / statsMesic.realizedTrialCount))}` : null },
                  { l: "Noví klienti", v: `${statsMesic.addedClients ?? 0}`, s: (statsMesic.addedClients > 0 && statsMesic.mktSpend > 0) ? `cost/nový klient ${fmtKcG(Math.round(statsMesic.mktSpend / statsMesic.addedClients))}` : null },
                  { l: "ROI marketingu", v: `${statsMesic.roiPct ?? 0} %`, s: `výnos ${fmtKcG(statsMesic.roiRevenue)} bez DPH`, tone: (statsMesic.roiPct || 0) >= 100 ? "text-emerald-600" : "text-rose-600" },
                ].map((r) => (
                  <div key={r.l} className="flex items-center gap-2 py-2 flex-wrap">
                    <span className="text-sm text-slate-600 flex-1 min-w-[9rem]">{r.l}</span>
                    {/* 21.7.2026: doplnky jako citelnejsi pill boxy (stejny styl jako SM/SEO blok) */}
                    {r.s && String(r.s).split(", ").map((cast) => (
                      <span key={cast} className="text-xs font-semibold tabular-nums px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 shrink-0">{cast}</span>
                    ))}
                    <span className={`text-sm font-bold tabular-nums ${r.tone || "text-slate-800"}`}>{r.v}</span>
                  </div>
                ))}
              </div>
            )}
            {/* 21.7.2026: SM marketing a SEO ze stejných dat jako CEO marketing report
                (/marketing). Okna jsou rolling posledních 30 dní vs. předchozích 30,
                proto poznámka v hlavičce — není to přesný kalendářní měsíc. */}
            {mktReport && (() => {
              const d30 = mktReport?.meta?.deltas_30d || {};
              const fb = d30.csv_last30 || {}, fbP = d30.csv_prev30 || {};
              const ig = d30.csv_ig_last30 || {}, igP = d30.csv_ig_prev30 || {};
              const gscC = mktReport?.last30?.gsc_current || {}, gscP = mktReport?.last30?.gsc_previous || {};
              const cislo = (x) => Number(x) || 0;
              const fmtN = (x) => new Intl.NumberFormat("cs-CZ").format(Math.round(Number(x) || 0));
              const trend = (cur, prev) => {
                const c = cislo(cur), pv = cislo(prev);
                if (pv <= 0) return null;
                const pct = Math.round(((c - pv) / pv) * 100);
                return { txt: `${pct >= 0 ? "+" : ""}${pct} %`, tone: pct >= 0 ? "text-emerald-600" : "text-rose-600" };
              };
              const radky = [
                { l: "SM dosah (FB + IG)", v: fmtN(cislo(fb.reach) + cislo(ig.reach)), s: `FB ${fmtN(fb.reach)}, IG ${fmtN(ig.reach)}`, t: trend(cislo(fb.reach) + cislo(ig.reach), cislo(fbP.reach) + cislo(igP.reach)) },
                { l: "SM interakce", v: fmtN(cislo(fb.engagement) + cislo(ig.engagement)), s: `FB ${fmtN(fb.engagement)}, IG ${fmtN(ig.engagement)}`, t: trend(cislo(fb.engagement) + cislo(ig.engagement), cislo(fbP.engagement) + cislo(igP.engagement)) },
                { l: "SM příspěvky", v: fmtN(cislo(d30.fb_posts_last30) + cislo(d30.ig_posts_last30)), s: `FB ${fmtN(d30.fb_posts_last30)}, IG ${fmtN(d30.ig_posts_last30)}`, t: trend(cislo(d30.fb_posts_last30) + cislo(d30.ig_posts_last30), cislo(d30.fb_posts_prev30) + cislo(d30.ig_posts_prev30)) },
                { l: "SM noví followers", v: fmtN(cislo(fb.new_followers) + cislo(ig.new_followers)), s: `FB ${fmtN(fb.new_followers)}, IG ${fmtN(ig.new_followers)}`, t: null },
                { l: "SEO kliknutí", v: fmtN(gscC.clicks), s: "ze Search Console", t: trend(gscC.clicks, gscP.clicks) },
                { l: "SEO zobrazení", v: fmtN(gscC.impressions), s: "ve výsledcích vyhledávání", t: trend(gscC.impressions, gscP.impressions) },
              ];
              return (
                <div className="mt-3 pt-2 border-t border-slate-100">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1">
                    SM a SEO, posledních 30 dní vs. předchozích 30, z CEO marketing reportu
                  </div>
                  <div className="divide-y divide-slate-50">
                    {radky.map((r) => (
                      <div key={r.l} className="flex items-center gap-2 py-2 flex-wrap">
                        <span className="text-sm text-slate-600 flex-1 min-w-[9rem]">{r.l}</span>
                        {r.s && String(r.s).split(", ").map((cast) => (
                          <span key={cast} className="text-xs font-semibold tabular-nums px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 shrink-0">{cast}</span>
                        ))}
                        {r.t && (
                          <span className={`text-xs font-bold tabular-nums px-2.5 py-1 rounded-full shrink-0 ${r.t.tone === "text-emerald-600" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>{r.t.txt}</span>
                        )}
                        <span className="text-sm font-bold tabular-nums text-slate-800">{r.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            </div>
          </KartaSeznam>
          {/* Nápady pro zlepšení (MĚSÍČNÍ) — 6.8.2026 přesunuto sem, na místo dlaždice
              nutriční konzultace (ta je nově pod Hodnocením od klientů).
              Co řádek, to nápad; ukládá se do sekce jako JSON [{t, a}]. */}
          {(() => {
            const idx = m.sekce.findIndex((s) => s.nadpis === "Nápady pro zlepšení");
            if (idx < 0) return null;
            const raw = String(m.sekce[idx].obsah || "").trim();
            let napady = [];
            if (raw.startsWith("[")) {
              try { const j = JSON.parse(raw); if (Array.isArray(j)) napady = j.map((x) => ({ t: String(x.t || ""), a: String(x.a || "") })).filter((x) => x.t); } catch (_) { /* řádky */ }
            }
            if (!napady.length && raw) napady = raw.split("\n").map((x) => x.trim()).filter(Boolean).map((t) => ({ t, a: "" }));
            const uloz = (list) => ulozSekci(idx, JSON.stringify(list));
            // Pridani napadu — sdilene pro tlacitko (mobil) i Enter (desktop).
            const pridejNapad = () => {
              if (!novyNapad.trim()) return;
              uloz([...napady, { t: novyNapad.trim(), a: mojeJmeno }]);
              setNovyNapad("");
            };
            return (
              <KartaSeznam
                ikona={Lightbulb}
                title="Nápady pro zlepšení"
                podtitulek="co řádek, to jeden nápad; pište kdykoli během měsíce"
                odznak={napady.length || undefined}
                odznakTon="zluta"
              >
                <div className="p-3 sm:p-4">
                {napady.length === 0 ? (
                  <div className="mb-2 text-sm text-slate-400">Zatím žádný nápad, sem s nimi.</div>
                ) : (
                  <div className="-mx-3 mb-3 divide-y divide-slate-100 sm:-mx-4">
                    {napady.map((n, i) => (
                      <div key={`${i}-${n.t}`} className="flex min-h-[56px] items-center gap-2.5 px-4 py-2">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-brand-gold/25">
                          <Lightbulb className="h-4 w-4 text-brand-950" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800">{n.t}</div>
                          {n.a && <div className="mt-1"><Stitek velikost="mala" ton="neutralni" className="demo-sensitive">{n.a}</Stitek></div>}
                        </div>
                        <button type="button" onClick={() => uloz(napady.filter((_, xi) => xi !== i))}
                          className="btn-ikona" aria-label="Smazat nápad" title="Smazat nápad"><X className="h-5 w-5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    className="field"
                    placeholder="Přidat nápad… (např. Zavést věrnostní program)"
                    value={novyNapad}
                    onChange={(e) => setNovyNapad(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && novyNapad.trim()) { e.preventDefault(); pridejNapad(); } }}
                  />
                  <button type="button" onClick={pridejNapad} disabled={!novyNapad.trim()}
                    className="shrink-0 w-9 h-9 rounded-xl bg-amber-500 text-white flex items-center justify-center hover:bg-amber-600 disabled:opacity-40 transition-colors"
                    title="Přidat nápad"><Send className="w-4 h-4" /></button>
                </div>
                </div>
              </KartaSeznam>
            );
          })()}
          </div>
          <div className="space-y-4">
          <KartaSeznam
            ikona={Star}
            title="Hodnocení od klientů"
            podtitulek={`${fmtMesicCz(hodnocenyMesic)}, z dotazníků v detailu klienta`}
            odznak={(detail?.hodnoceniKlientu || []).length || undefined}
          >
            <div className="p-3 sm:p-4">
            {(detail?.hodnoceniKlientu || []).length === 0 ? (
              <div className="text-sm text-slate-400">Tento měsíc zatím žádné nové hodnocení. Vyplňuje se v detailu klienta (sekce Hodnocení spolupráce).</div>
            ) : (
              <div className="space-y-1.5">
                {detail.hodnoceniKlientu.map((h) => {
                  const odp = h.odpovedi || {};
                  const celkove = parseFloat(odp.celkove);
                  const doporuc = String(odp.doporuceni || "").toLowerCase();
                  // Podněty, které nesmí zapadnout: textové poznámky (mimo triviální "ne/nic"),
                  // nízká skóre a odpovědi "Ne" u ano/ne otázek (kromě nutrice).
                  const trivialni = (t) => ["ne", "nic", "ne.", "nic.", "-", "zatim ne", "zatím ne", "zatim nic", "zatím nic", "zadne", "žádné"].includes(String(t).trim().toLowerCase());
                  const podnety = HODNOCENI_OTAZKY
                    .filter((o) => o.typ === "text" && o.k !== "jak_dlouho" && o.k !== "prinos" && o.k !== "zmeny")
                    .map((o) => ({ o, v: String(odp[o.k] || "").trim() }))
                    .filter((x) => x.v && !trivialni(x.v));
                  const negativa = HODNOCENI_OTAZKY.filter((o) =>
                    (o.typ === "anone" && o.k !== "nutrice" && String(odp[o.k] || "").trim().toLowerCase() === "ne")
                    || (o.typ === "skala5" && parseFloat(odp[o.k]) <= 3 && !Number.isNaN(parseFloat(odp[o.k])))
                    || (o.typ === "skala10" && parseFloat(odp[o.k]) <= 5 && !Number.isNaN(parseFloat(odp[o.k]))));
                  const maSignal = podnety.length > 0 || negativa.length > 0;
                  const open = openHodnoceni === h.id;
                  return (
                    <div key={h.id} className={`rounded-xl border overflow-hidden ${maSignal ? "border-amber-200 bg-amber-50/40" : "border-slate-100"}`}>
                      <button type="button" onClick={() => setOpenHodnoceni(open ? null : h.id)}
                        className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-slate-50/70 transition-colors">
                        <ChevronRight className={`w-4 h-4 text-slate-300 transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
                        {/* 21.7.2026: celé jméno klientky (bez truncate, klidně na 2 řádky);
                            badge "k probrání" se přesunul do rozbalovacího souhrnu níž. */}
                        <span className="text-sm font-semibold text-slate-700 flex-1 min-w-0 break-words demo-sensitive">{h.klient_name}</span>
                        {h.trener && <span className="text-[11px] text-slate-400 shrink-0 demo-sensitive hidden sm:inline">{h.trener}</span>}
                        {doporuc === "ano" && <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 shrink-0">doporučí</span>}
                        {!Number.isNaN(celkove) && (
                          <span className="inline-flex items-center gap-0.5 shrink-0">
                            {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(celkove) ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />)}
                          </span>
                        )}
                        <span className="text-[11px] text-slate-400 shrink-0">{fmtCz(h.datum)}</span>
                      </button>
                      {/* 21.7.2026: souhrn "k probrání" je rozbalovací (dřív se vypisoval celý
                          a karta byla dlouhá) — sbalený řádek nese počet věcí k probrání. */}
                      {!open && maSignal && (
                        <div className="px-3 pb-2">
                          <button type="button"
                            onClick={(e) => { e.stopPropagation(); setOpenPodnety(openPodnety === h.id ? null : h.id); }}
                            className="w-full flex items-center gap-1.5 text-xs font-bold text-amber-800 bg-amber-100/70 hover:bg-amber-100 border border-amber-200 rounded-xl px-2 py-1.5 transition-colors"
                            title="Poznámky nebo nižší hodnocení, probrat na meetingu">
                            <ChevronRight className={`w-3.5 h-3.5 transition-transform shrink-0 ${openPodnety === h.id ? "rotate-90" : ""}`} />
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {podnety.length + negativa.length} k probrání
                          </button>
                          {openPodnety === h.id && (
                            <div className="mt-1 space-y-1">
                              {podnety.map(({ o, v }) => (
                                <div key={o.k} className="text-xs text-amber-900 bg-white/70 border border-amber-100 rounded-xl px-2 py-1">
                                  <span className="font-semibold">{o.q}</span>: {v}
                                </div>
                              ))}
                              {negativa.map((o) => (
                                <div key={o.k} className="text-xs text-rose-800 bg-rose-50 border border-rose-100 rounded-xl px-2 py-1 flex items-center gap-2">
                                  <span className="font-semibold flex-1">{o.q}</span>
                                  <OdpovedPill otazka={o} hodnota={odp[o.k]} />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Rozklik: celý dotazník */}
                      {open && (
                        <div className="px-4 pb-3 pt-1 space-y-1 border-t border-slate-100 bg-white">
                          {HODNOCENI_OTAZKY.filter((o) => odp[o.k] != null && String(odp[o.k]).trim() !== "").map((o) => (
                            <div key={o.k} className="flex items-start gap-3 py-1">
                              <span className="text-xs text-slate-500 flex-1 min-w-0">{o.q}</span>
                              <span className="shrink-0 max-w-[55%] text-right"><OdpovedPill otazka={o} hodnota={odp[o.k]} /></span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          </KartaSeznam>
          {/* Nutriční konzultace — základní statistika za hodnocený měsíc.
              6.8.2026: posunuto pod Hodnocení od klientů (pravý sloupec).
              Trenér = trenér KLIENTA, ne kdo konzultaci vedl (to je nutriční specialistka). */}
          {(() => {
            const nk = detail?.nutriKonzultace || { celkem: 0, unikatnich: 0, klienti: [] };
            return (
              <KartaSeznam
                ikona={Salad}
                title="Nutriční konzultace"
                podtitulek={`${fmtMesicCz(hodnocenyMesic)}, z rezervací`}
                odznak={nk.celkem || undefined}
              >
                <div className="p-3 sm:p-4">
                {nk.celkem === 0 ? (
                  <div className="text-sm text-slate-400">Tento měsíc žádná nutriční konzultace.</div>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-2 mb-2.5">
                      <span className="text-xs font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1">Celkem konzultací: {nk.celkem}</span>
                      <span className="text-xs font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1">Unikátních klientů: {nk.unikatnich}</span>
                    </div>
                    <div className="space-y-1.5">
                      {nk.klienti.map((k, i) => (
                        <div key={`${i}-${k.klient}`} className="flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-50 border border-slate-200">
                          <span className="text-sm font-semibold text-slate-800 flex-1 min-w-0 demo-sensitive">{k.klient}</span>
                          <span className={`text-xs shrink-0 ${k.trener ? "text-slate-500" : "text-amber-600"}`}>{k.trener ? `trenér ${k.trener}` : "bez trenéra"}</span>
                          <span className="text-[11px] font-semibold text-slate-400 shrink-0 tabular-nums whitespace-nowrap">{fmtCz(k.datum)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                </div>
              </KartaSeznam>
            );
          })()}
          </div>
        </div>
      )}

      {/* Úkoly na příští týden/měsíc + Odloženo vedle sebe (kvartál a rok bez odloženo).
          MĚSÍČNÍ meeting plán ani odloženo neřeší — to je věc týdenního. */}
      {m.typ !== "mesicni" && (
      <div className={`grid grid-cols-1 gap-4 items-start ${m.typ === "tydenni" ? "lg:grid-cols-2" : ""}`}>
        <KartaSeznam
          ikona={Link2}
          title={m.typ === "tydenni" ? "Úkoly na příští týden" : m.typ === "mesicni" ? "Úkoly na tento měsíc" : "Úkoly z meetingu"}
          podtitulek={planDo ? `plánujeme čtvrtek → čtvrtek, termín do ${fmtCz(planDo)}` : undefined}
          odznak={ukolyPlan.length || undefined}
        >
          <div className="p-3 sm:p-4">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {ukolyPlan.length > 0 && tlacitkoSbalit(hromPlan, setHromPlan)}
          </div>
          {/* Systémová kontrola: bez pilotů a deadlinů meeting nejde uzavřít */}
          {m.typ === "tydenni" && ukolyPlan.length > 0 && (
            ukolyBezDetailu.length === 0 ? (
              <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-2.5 py-1.5 mb-2">
                <CheckCircle2 className="w-3.5 h-3.5" /> Všechny úkoly mají pilota a deadline, meeting jde uzavřít.
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-2.5 py-1.5 mb-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {ukolyBezDetailu.length === 1 ? "1 úkol nemá" : `${ukolyBezDetailu.length} úkoly nemají`} pilota nebo deadline, bez doplnění meeting neuzavřeš.
              </div>
            )
          )}
          {ukolyPlan.length === 0 ? (
            <div className="text-sm text-slate-400 py-2">Zatím žádné úkoly.</div>
          ) : (
            <div className="mb-3">
              {skupinyUkolu(ukolyPlan, (u) => radekUkolu(u, m.typ === "tydenni"), false, hromPlan)}
            </div>
          )}
          {/* Hotové v tomto týdnu MIMO plán meetingu — propisují se automaticky */}
          {/* „Hotové mimo meeting" přesunuto do „Úkoly z minulého týdne" (retrospektiva, 21.7.2026) */}
          {/* Nový úkol / projekt z meetingu — otevře plný formulář (jako na boardu), s vazbou na meeting */}
          <div className="border-t border-slate-100 pt-3 flex gap-2">
            <button type="button" onClick={() => setShowAddTask({ jeProjekt: false })}
              className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-brand-700 bg-brand-50 border border-brand-200 rounded-xl px-3 py-2 hover:bg-brand-100 transition-colors">
              <Plus className="w-4 h-4" /> Přidat úkol
            </button>
            <button type="button" onClick={() => setShowAddTask({ jeProjekt: true })}
              className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-plum-700 bg-plum-50 border border-plum-200 rounded-xl px-3 py-2 hover:bg-plum-100 transition-colors">
              <Plus className="w-4 h-4" /> Přidat projekt
            </button>
          </div>
          {/* Připojit existující — vyhledávací picker místo dlouhého selectu */}
          {attachable.length > 0 && (
            <div className="mt-2">
              <UkolPicker tasks={attachable} value="" onChange={(id) => attachTask(id)}
                placeholder="Připojit existující úkol… (hledej názvem)" className="w-full" />
            </div>
          )}
          {showAddTask && (
            <AddTaskModal
              people={lideJmena}
              tagSuggestions={[]}
              kategorie={kategorieList}
              canManage={!!ukolyData?.canManage}
              defaultJeProjekt={showAddTask.jeProjekt === true}
              defaultStatus={showAddTask.odklad === true ? "zasobnik" : m.typ === "mesicni" ? "mesicni" : "tydenni"}
              existingTasks={ukolyData?.tasks || []}
              extraTaskFields={{ meeting_id: meetingId, ...(showAddTask.odklad === true ? { odlozeno: true } : {}) }}
              onClose={() => setShowAddTask(false)}
              onCreated={() => { load(); refetchUkoly(); }}
            />
          )}
          {odkladDialog && (
            <OdkladDialog ukol={odkladDialog} onClose={() => setOdkladDialog(null)}
              onSubmit={(duvod, misto) => pozadatOdklad(odkladDialog, duvod, misto)} />
          )}
          {openUkol && (
            <TaskDetailModal
              taskId={openUkol}
              people={lideJmena}
              fotoByName={fotoByName}
              tagSuggestions={[]}
              kategorie={kategorieList}
              canManage={!!ukolyData?.canManage}
              canDelete={false}
              allTasks={ukolyData?.tasks || []}
              onOpenTask={setOpenUkol}
              onClose={() => setOpenUkol(null)}
              onChanged={() => { load(); refetchUkoly(); }}
              onDelete={() => {}}
            />
          )}
          </div>
        </KartaSeznam>

        {m.typ === "tydenni" && (<>
        {/* Odloženo — co jsme na meetingu odložili. Backlog (nápady na později)
            je jiná věc a zůstává v boardu Operations > Úkoly. */}
        <KartaSeznam
          ikona={Archive}
          title="Odloženo"
          podtitulek="vrací se do každého dalšího meetingu, dokud úkol nenaplánuješ"
          odznak={ukolyOdklad.length || undefined}
        >
          <div className="p-3 sm:p-4">
          {ukolyOdklad.length > 0 && <div className="mb-2">{tlacitkoSbalit(hromOdklad, setHromOdklad)}</div>}
          {ukolyOdklad.length === 0 ? (
            <div className="text-sm text-slate-400 py-1">Zatím nic neodkládáme.</div>
          ) : (
            <div className="mb-3">
              {skupinyUkolu(ukolyOdklad, (u) => radekUkolu(u, false, (
                <button type="button" onClick={(e) => { e.stopPropagation(); zaraditDoTydne(u); }}
                  className="btn btn-primary shrink-0"
                  title={m.typ === "mesicni" ? "Přesunout z Odloženo do úkolů na tento měsíc" : "Přesunout z Odloženo do úkolů na příští týden"}>
                  {m.typ === "mesicni" ? "Zařadit" : "Do týdne"}
                </button>
              )), false, hromOdklad)}
            </div>
          )}
          <div className="border-t border-slate-100 pt-3">
            <button type="button" onClick={() => setShowAddTask({ jeProjekt: false, odklad: true })}
              className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 hover:bg-slate-100 transition-colors">
              <Plus className="w-4 h-4" /> Přidat úkol do Odloženo
            </button>
            {attachable.length > 0 && (
              <div className="mt-2">
                <UkolPicker tasks={attachable} value="" onChange={(id) => attachTask(id, { odklad: true })}
                  vsechny placeholder="Odložit existující úkol… (hledej názvem)" className="w-full" />
              </div>
            )}
          </div>
          </div>
        </KartaSeznam>
        </>)}
      </div>
      )}

      {/* 7. Poznámky — per role (styl jako měsíční „Poznámky k meetingu"), sdílený
          render renderPoznamkyPerRole(): checkbox + autor, role = sbalovací sekce.
          Neodškrtnuté blokují uzavření (týdenní). Umístění beze změny (dole). */}
      {m.typ !== "mesicni" && (
      <KartaSeznam
        ikona={StickyNote}
        title="Poznámky"
        podtitulek={m.typ === "tydenni" ? "každý bod se probere a odškrtne, jinak meeting nejde uzavřít" : "každý bod se na meetingu probere a odškrtne"}
        odznak={poznamky.length > 0 ? `${poznamky.length - poznamkyNeprobrane.length} z ${poznamky.length}` : undefined}
        odznakTon={poznamky.length > 0 && poznamkyNeprobrane.length === 0 ? "zluta" : "bila"}
      >
        <div className="p-3 sm:p-4">
        {renderPoznamkyPerRole()}
        </div>
      </KartaSeznam>
      )}

      {/* Zápis po sekcích — otázky pro role (CSM/Manager/Owner) seskupené do karet.
          Nová měsíční šablona má jen úspěchy + nápady (vlastní karty) → zápis se skryje. */}
      {!(m.typ === "mesicni" && m.sekce.every((s) => ["Co se nám minulý měsíc povedlo?", "Nápady pro zlepšení"].includes(s.nadpis))) && (
      <KartaSeznam
        ikona={FileText}
        title="Zápis z meetingu"
      >
        <div className="p-3 sm:p-4">
        {(() => {
          const roleRe = /^(.*) \((CSM|Manager|Owner)\)$/;
          // Sekce nahrazené strukturovanými kartami (u starých meetingů se skryjí, jen když jsou prázdné).
          const skryte = m.typ === "mesicni"
            ? ["Poznámky", "Jaký byl celkový obrat? (za měsíc, za celý rok)", "Jak jsme splnili cíle předchozího měsíce a proč? (cíl + BONUS)", "Jak jsme splnili priority z předchozího měsíce?", "Priority pro další měsíc", "Cíle pro další měsíc (cíl + BONUS)", "Jaká přibyla hodnocení od klientů za minulý měsíc?"]
            : ["Stav úkolů z minulého týdne", "Na co se zaměříme příští týden? (úkoly)", "Co odkládáme?", "Poznámky"];
          const polozky = [];
          const zpracovane = new Set();
          m.sekce.forEach((s, i) => {
            if (zpracovane.has(i)) return;
            // Sekce nahrazené strukturovanými kartami skryj (u starých meetingů jen pokud jsou prázdné).
            const USPECHY_SKIP = { tydenni: "Co se nám povedlo minulý týden?", mesicni: "Co se nám minulý měsíc povedlo?", kvartalni: "Co se povedlo" };
            if (s.nadpis === USPECHY_SKIP[m.typ]) { zpracovane.add(i); return; }
            // Nápady pro zlepšení mají u měsíčního vlastní kartu (💡) v gridu výš.
            if (m.typ === "mesicni" && s.nadpis === "Nápady pro zlepšení") { zpracovane.add(i); return; }
            if (["tydenni", "mesicni"].includes(m.typ) && !String(s.obsah || "").trim() && skryte.includes(s.nadpis)) { zpracovane.add(i); return; }
            const mm = roleRe.exec(s.nadpis || "");
            if (mm) {
              const zaklad = mm[1];
              const skupina = [];
              m.sekce.forEach((s2, j) => {
                const m2 = roleRe.exec(s2.nadpis || "");
                if (m2 && m2[1] === zaklad) { skupina.push({ obsah: s2.obsah, idx: j, role: m2[2] }); zpracovane.add(j); }
              });
              polozky.push({ typ: "skupina", zaklad, skupina });
            } else {
              zpracovane.add(i);
              polozky.push({ typ: "single", s, idx: i });
            }
          });
          const ROLE_RADEK = {
            CSM: { pill: "bg-white/80 text-emerald-700", hlava: "bg-emerald-100 text-emerald-700", radek: "from-emerald-100/80 via-emerald-50 to-white border-emerald-200", kolo: "from-emerald-300 to-emerald-500 shadow-emerald-200" },
            Manager: { pill: "bg-white/80 text-sky-700", hlava: "bg-sky-100 text-sky-700", radek: "from-sky-100/80 via-sky-50 to-white border-sky-200", kolo: "from-sky-300 to-sky-500 shadow-sky-200" },
            Owner: { pill: "bg-white/80 text-brand-700", hlava: "bg-brand-100 text-brand-700", radek: "from-brand-100/80 via-brand-50 to-white border-brand-200", kolo: "from-brand-300 to-brand-500 shadow-brand-200" },
          };
          // Stejný princip jako Úspěchy: barevné gradient řádky, pole roste podle
          // délky odpovědi. 5.9.2026: ikona ze vzorníku místo emoji u typu otázky.
          const ikonaOtazky = (zaklad) => /\bAI\b/i.test(zaklad) ? Bot : /situaci/i.test(zaklad) ? Lightbulb : /shrnut/i.test(zaklad) ? MessageSquare : StickyNote;
          const rosti = (el) => { el.style.height = "auto"; el.style.height = `${Math.max(28, el.scrollHeight)}px`; };
          return polozky.map((p, k) => p.typ === "skupina" ? (
            <div key={k}>
              <div className="mb-2 flex items-center gap-1.5 text-sm font-bold text-slate-800">
                {(() => { const IkonaO = ikonaOtazky(p.zaklad); return <IkonaO className="w-4 h-4 shrink-0 text-slate-400" />; })()}
                {p.zaklad}
              </div>
              {/* Dropdown jako Poznámky: role = sbalovací sekce, uvnitř víc poznámek.
                  Přidávat/upravovat smí jen daná role (owner do všech, zapíše se jeho jméno). */}
              <div className="space-y-2">
                {p.skupina.map((sl) => {
                  const rs = ROLE_RADEK[sl.role] || ROLE_RADEK.Owner;
                  const smiPsat = jeOwner || (String(sl.role).toLowerCase() === mojeRole);
                  const list = parseZapisList(sl.obsah);
                  const otevreno = openZapis[sl.idx] === true;
                  return (
                    <div key={sl.idx} className="border border-slate-100 rounded-xl overflow-hidden">
                      <button type="button" onClick={() => setOpenZapis((r) => ({ ...r, [sl.idx]: !otevreno }))}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${rs.hlava || rs.pill}`}>{sl.role}</span>
                        <span className="text-[11px] font-semibold text-slate-400 tabular-nums">({list.length})</span>
                        <span className="flex-1 border-t border-slate-100" />
                        <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform ${otevreno ? "" : "-rotate-90"}`} />
                      </button>
                      {otevreno && (
                        <div className="px-3 pb-3 space-y-2">
                          {list.length === 0 && !smiPsat && <div className="text-sm text-slate-400">Zatím nic.</div>}
                          {list.map((it, ii) => {
                            const smiUpravit = jeOwner || (it.a && it.a === mojeJmeno) || smiPsat;
                            const upravuji = editZapis && editZapis.idx === sl.idx && editZapis.i === ii;
                            return (
                              <div key={ii} className={`group flex flex-wrap sm:flex-nowrap items-start gap-x-3 gap-y-1.5 rounded-xl px-3 py-2.5 bg-gradient-to-r border shadow-sm hover:shadow-md transition-all ${rs.radek}`}>
                                <span className={`w-7 h-7 mt-0.5 rounded-full bg-gradient-to-br text-white flex items-center justify-center shadow-md shrink-0 ${rs.kolo}`}>{(() => { const IkonaK = ikonaOtazky(p.zaklad); return <IkonaK className="w-3.5 h-3.5" />; })()}</span>
                                {upravuji ? (
                                  <>
                                  <input autoFocus
                                    className="field flex-1 min-w-0 font-medium mt-0.5"
                                    value={editZapis.text}
                                    onChange={(e) => setEditZapis({ idx: sl.idx, i: ii, text: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && editZapis.text.trim()) { e.preventDefault(); ulozZapisList(sl.idx, list.map((x, xi) => (xi === ii ? { ...x, t: editZapis.text.trim() } : x))); setEditZapis(null); }
                                      if (e.key === "Escape") setEditZapis(null);
                                    }}
                                    onBlur={() => setEditZapis(null)} />
                                  {/* onPointerDown preventDefault: neshodi focus inputu (onBlur by editaci zrusil driv, nez dorazi click) */}
                                  <button type="button" title="Uložit úpravu"
                                    onPointerDown={(e) => e.preventDefault()}
                                    onClick={() => { if (editZapis.text.trim()) ulozZapisList(sl.idx, list.map((x, xi) => (xi === ii ? { ...x, t: editZapis.text.trim() } : x))); setEditZapis(null); }}
                                    className="btn btn-primary mt-0.5 shrink-0"><Check className="w-4 h-4" /></button>
                                  </>
                                ) : (
                                  <span className="text-[15px] font-medium text-slate-800 order-last sm:order-none w-full sm:w-auto sm:flex-1 min-w-0 whitespace-pre-wrap leading-relaxed">{it.t}</span>
                                )}
                                {it.a && <span className={`text-[11px] font-bold px-2 py-0.5 mt-0.5 rounded-full border border-white shrink-0 mr-auto sm:mr-0 demo-sensitive ${rs.pill}`}>{it.a}</span>}
                                {smiUpravit && !upravuji && (
                                  <button type="button" onMouseDown={(e) => { e.preventDefault(); setEditZapis({ idx: sl.idx, i: ii, text: it.t }); }}
                                    className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 p-0.5 mt-0.5 text-slate-400 hover:text-slate-700 shrink-0 transition-opacity" title="Upravit"><Pencil className="w-4 h-4" /></button>
                                )}
                                {smiUpravit && (
                                  <button type="button" onClick={() => ulozZapisList(sl.idx, list.filter((_, xi) => xi !== ii))}
                                    className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 p-0.5 mt-0.5 text-slate-300 hover:text-rose-600 shrink-0 transition-opacity" title="Smazat"><X className="w-4 h-4" /></button>
                                )}
                              </div>
                            );
                          })}
                          {smiPsat && (
                            <div className="flex items-center gap-2">
                              <input className="field"
                                placeholder="Přidej poznámku…"
                                value={novyZapis[sl.idx] || ""}
                                onChange={(e) => setNovyZapis((r) => ({ ...r, [sl.idx]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === "Enter" && (novyZapis[sl.idx] || "").trim()) { e.preventDefault(); pridejZapis(sl.idx, list); } }} />
                              <button type="button" onClick={() => pridejZapis(sl.idx, list)} disabled={!(novyZapis[sl.idx] || "").trim()}
                                className="shrink-0 w-9 h-9 rounded-xl bg-brand-600 text-white flex items-center justify-center hover:bg-brand-700 disabled:opacity-40 transition-colors"
                                title="Přidat poznámku"><Send className="w-4 h-4" /></button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div key={k}>
              <label className="text-sm font-semibold text-slate-700">{p.s.nadpis}</label>
              <textarea
                className="field min-h-[70px] mt-1"
                defaultValue={p.s.obsah}
                placeholder="Zapiš poznámky…"
                onBlur={(e) => { if (e.target.value !== p.s.obsah) ulozSekci(p.idx, e.target.value); }}
              />
            </div>
          ));
        })()}
        </div>
      </KartaSeznam>
      )}

      <ConfirmDialog
        open={pendingDelete}
        title="Smazat meeting?"
        description={`Meeting "${m.nazev}" se smaže včetně zápisu. Navázané úkoly zůstávají v boardu, jen ztratí vazbu.`}
        confirmLabel="Smazat"
        variant="danger"
        onConfirm={async () => {
          setPendingDelete(false);
          const ok = await post({ action: "delete", id: m.id }, { reload: false });
          if (ok) onClose();
        }}
        onCancel={() => setPendingDelete(false)}
      />
    </Stranka>
  );
}

export default function OperationsMeetingy() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("open");
  const { data, refetch } = useApiData("/api/operations-meetingy");
  const [creating, setCreating] = useState(false);
  const [showNovyMenu, setShowNovyMenu] = useState(false);
  // Filtr (multiselect, drží se v localStorage): default otevřené týdenní + měsíční,
  // kvartál a rok schované, uzavřené schované.
  const [filtr, setFiltr] = useState(() => {
    try {
      const f = JSON.parse(localStorage.getItem("fw40_meetingy_filtr"));
      if (f && Array.isArray(f.typy) && Array.isArray(f.stavy)) return f;
    } catch { /* výchozí */ }
    return { typy: ["tydenni", "mesicni"], stavy: ["otevreno"] };
  });
  useEffect(() => {
    try { localStorage.setItem("fw40_meetingy_filtr", JSON.stringify(filtr)); } catch { /* private mode */ }
  }, [filtr]);
  // Stav je segmentový přepínač (Vše / Otevřené / Uzavřené), uvnitř se dál
  // ukládá jako pole `stavy`, aby zůstalo uložené nastavení z dřívějška.
  const stavFiltr = filtr.stavy.length === 2 ? "vse" : (filtr.stavy[0] || "vse");
  const nastavStav = (klic) => setFiltr((f) => ({
    ...f,
    stavy: klic === "vse" ? ["otevreno", "hotovo"] : [klic],
  }));
  const jeOwner = String(getCurrentUser()?.role || "").toLowerCase() === "owner";
  // Filtr archivu uzavřených (rok / měsíc) — u desítek starých meetingů.
  const [filtrRok, setFiltrRok] = useState("vse");
  const [filtrMesic, setFiltrMesic] = useState("vse");

  const openDetail = (id) => setSearchParams(id ? { open: id } : {});

  const createMeeting = async (typ) => {
    setCreating(true);
    setShowNovyMenu(false);
    try {
      const r = await apiFetch("/api/operations-meetingy", { method: "POST", body: JSON.stringify({ action: "create", typ }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Vytvoření selhalo");
      refetch();
      openDetail(j.id);
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
    finally { setCreating(false); }
  };

  if (openId) {
    return <MeetingDetail meetingId={openId} onClose={() => openDetail(null)} onChanged={refetch} />;
  }

  // Řazení otevřených: typy v pevném pořadí (týdenní → roční), uvnitř dle data.
  const TYP_PORADI = { tydenni: 0, mesicni: 1, kvartalni: 2, rocni: 3 };
  const serad = (arr) => [...arr].sort((a, b) =>
    ((TYP_PORADI[a.typ] ?? 9) - (TYP_PORADI[b.typ] ?? 9)) || String(b.datum || "").localeCompare(String(a.datum || "")));
  const otevrene = serad((data?.otevrene || []).filter((m) => filtr.typy.includes(m.typ)));
  // Uzavřené = archiv: čistě od nejnovějšího + filtr roku a měsíce (u desítek meetingů).
  const hotoveVse = (data?.hotove || []).filter((m) => filtr.typy.includes(m.typ));
  const rokyUzavrenych = [...new Set(hotoveVse.map((m) => String(m.datum || "").slice(0, 4)).filter(Boolean))].sort().reverse();
  const hotove = hotoveVse
    .filter((m) => (filtrRok === "vse" || String(m.datum || "").slice(0, 4) === filtrRok)
      && (filtrMesic === "vse" || String(m.datum || "").slice(5, 7) === filtrMesic))
    .sort((a, b) => String(b.datum || "").localeCompare(String(a.datum || "")));
  const showOtevrene = filtr.stavy.includes("otevreno");
  const showUzavrene = filtr.stavy.includes("hotovo");

  // Jeden meeting = jeden řádek: ikona v tónu typu, název, štítky a tlačítko
  // s šipkou (stejná ikona pro „otevřít detail" v celé aplikaci).
  const MeetingRadek = ({ m, uzavreny = false }) => {
    const typ = TYP_META[m.typ] || { label: m.typ, ton: "neutralni" };
    const t = tonTypu(typ.ton);
    const agenda = m.agenda || [];
    const agendaDone = agenda.filter((a) => a.done).length;
    const pozn = m.poznamky || [];
    const poznDone = pozn.filter((p) => p.done).length;
    const uspechySekce = (m.sekce || []).find((s) => String(s.nadpis || "").toLowerCase().includes("povedlo"));
    const uspechy = uspechySekce ? String(uspechySekce.obsah || "").split("\n").map((x) => x.trim()).filter(Boolean).length : 0;
    return (
      <div className="flex min-h-[72px] items-center gap-2.5 px-3 py-2.5 sm:px-4">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${uzavreny ? "bg-slate-100" : t.pole}`}>
          {uzavreny ? <CheckCircle2 className="h-5 w-5 text-slate-500" /> : <CalendarCheck className={`h-5 w-5 ${t.text}`} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-bold text-slate-900">{m.nazev}</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            <Stitek velikost="mala" ton={typ.ton}>{typ.label}</Stitek>
            <Stitek velikost="mala" ton="neutralni">{fmtCz(m.datum)}</Stitek>
            {agenda.length > 0 && (
              <Stitek velikost="mala" ton={agendaDone === agenda.length ? "ok" : "pozor"}>agenda {agendaDone}/{agenda.length}</Stitek>
            )}
            {(m.ukolyCelkem || 0) > 0 && (
              <Stitek velikost="mala" ton={m.ukolyHotovo === m.ukolyCelkem ? "ok" : "pozor"}>úkoly {m.ukolyHotovo}/{m.ukolyCelkem}</Stitek>
            )}
            {pozn.length > 0 && (
              <Stitek velikost="mala" ton={poznDone === pozn.length ? "ok" : "pozor"}>poznámky {poznDone}/{pozn.length}</Stitek>
            )}
            {uspechy > 0 && <Stitek velikost="mala" ton="zlata">{uspechy} úspěchů</Stitek>}
          </div>
        </div>
        <button type="button" onClick={() => openDetail(m.id)} aria-label="Otevřít meeting" title="Otevřít meeting" className="btn-ikona">
          <ArrowUpRight className="h-5 w-5" />
        </button>
      </div>
    );
  };

  return (
    <Stranka sirka="uzka">
      {/* 4.9.2026 (fáze 4a): gradientová hlavička, filtr podle vzoru z PM,
          seznamy jako karty s lištou. Data a chování beze změny. */}
      <GradientHeader
        title="Meetingy"
        icon={CalendarCheck}
        akce={jeOwner ? [{ klic: "novy", ikona: Plus, popis: "Nový meeting", onClick: () => setShowNovyMenu(true), disabled: creating }] : undefined}
      />

      {/* Nový meeting: výběr typu vyjede zespodu, ne jako drobná rozbalovací nabídka. */}
      <VyberSeznamu
        open={showNovyMenu}
        onClose={() => setShowNovyMenu(false)}
        title="Jaký meeting založit"
        ikona={CalendarCheck}
        onChange={(t) => createMeeting(t)}
        skupiny={[{
          nadpis: "Typ meetingu",
          polozky: Object.keys(TYP_META).map((t) => ({
            key: t, label: `${TYP_META[t].label} meeting`, ikona: CalendarCheck, ton: TYP_META[t].ton,
          })),
        }]}
      />

      {/* Filtr: typ je celoplošný řádek se zaškrtávacími políčky, stav segment. */}
      <PruhFiltru>
        <FiltrRadek
          stitek="Typ"
          popisVse="Všechny typy"
          value={filtr.typy}
          onChange={(v) => setFiltr((f) => ({ ...f, typy: v }))}
          items={Object.keys(TYP_META)
            .filter((t) => jeOwner || !["kvartalni", "rocni"].includes(t))
            .map((t) => ({ key: t, label: TYP_META[t].label, ton: TYP_META[t].ton }))}
        />
        <Segment
          aria-label="Stav meetingů"
          value={stavFiltr}
          onChange={nastavStav}
          items={[
            { key: "vse", label: "Vše" },
            { key: "otevreno", label: "Otevřené" },
            { key: "hotovo", label: "Uzavřené" },
          ]}
        />
      </PruhFiltru>

      {showOtevrene && (
        <KartaSeznam ikona={CalendarCheck} title="Otevřené meetingy" odznak={otevrene.length}>
          {otevrene.length === 0 ? (
            <EmptyState
              icon={CalendarCheck}
              title="Žádný otevřený meeting vybraných typů"
              hint="Uprav filtr, nebo založ nový přes ikonu v hlavičce."
            />
          ) : (
            <div className="divide-y divide-slate-100">
              {otevrene.map((m) => <MeetingRadek key={m.id} m={m} />)}
            </div>
          )}
        </KartaSeznam>
      )}

      {showUzavrene && (
        <KartaSeznam ikona={Archive} title="Uzavřené meetingy" odznak={hotove.length}>
          {/* Archiv: filtr roku a měsíce, ať se dá listovat i v desítkách meetingů */}
          <div className="flex gap-2 border-b border-slate-100 p-3">
            <select className="field min-w-0 flex-1" value={filtrRok} onChange={(e) => setFiltrRok(e.target.value)}>
              <option value="vse">Všechny roky</option>
              {rokyUzavrenych.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="field min-w-0 flex-1" value={filtrMesic} onChange={(e) => setFiltrMesic(e.target.value)}>
              <option value="vse">Celý rok</option>
              {MESICE_CZ.map((nazev, i) => <option key={i} value={String(i + 1).padStart(2, "0")}>{nazev}</option>)}
            </select>
          </div>
          {hotove.length === 0 ? (
            <EmptyState icon={Archive} title="Žádný uzavřený meeting pro vybraný filtr" />
          ) : (
            <div className="divide-y divide-slate-100">
              {hotove.map((m) => <MeetingRadek key={m.id} m={m} uzavreny />)}
            </div>
          )}
        </KartaSeznam>
      )}

      {!showOtevrene && !showUzavrene && (
        <div className="glass">
          <EmptyState icon={CalendarCheck} title="Vyber stav" hint="Přepni nahoře na Otevřené, Uzavřené nebo Vše." />
        </div>
      )}
    </Stranka>
  );
}

